import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { MultiSelect } from '../components/MultiSelect';
import HierarchicalSelect from '../components/HierarchicalSelect';
import { confirmDialog } from '../store/useConfirmStore';
import { showAlert } from '../store/useAlertStore';
import { knowledgeApi, KnowledgeEntry, CreateKnowledgeRequest, UpdateKnowledgeRequest, MetricCondition } from '../api/knowledge';
import { adTagsApi, TagDefinition, TagOption } from '../api/adTags';
import { authAccountsApi, PlatformAccountSimple } from '../api/authAccounts';
import { useAppStore } from '../store/useAppStore';
import { TableLoadingState } from '../components/TableLoadingOverlay';
import Pagination from '../components/Pagination';

const catMeta: Record<string, { label: string; color: string; bg: string }> = {
  bid:      { label: '出价规则', color: 'var(--amber)',  bg: 'var(--amber-bg)' },
  creative: { label: '素材经验', color: 'var(--pink)',   bg: 'var(--pink-bg)' },
  audience: { label: '受众洞察', color: 'var(--cyan)',   bg: 'var(--cyan-bg)' },
  market:   { label: '市场经验', color: 'var(--violet)', bg: 'var(--violet-bg)' },
  keyword:  { label: '关键词经验', color: 'var(--green)',  bg: 'var(--green-bg)' },
  product:  { label: '商品洞察', color: 'var(--orange)', bg: 'var(--orange-bg)' },
  monitor:  { label: '数据监控', color: 'var(--blue)',   bg: 'var(--blue-bg)' },
};

const platformIcons: Record<string, string> = {
  tiktok: 'TT', facebook: 'FB', google: 'GG', all: '全平台',
};

interface TriggerCondition {
  metric: string;
  operator: string;
  value: string;
  valueMax?: string;
  valueRef: 'abs' | 'target_pct' | 'avg_pct' | 'market_avg_pct' | 'account_avg_pct';
  window?: string;
}

interface TriggerConditionGroup {
  logic: 'AND' | 'OR';
  conditions: TriggerCondition[];
}

interface TriggerAction {
  action: string;
  actionCountryFilter?: string;
  actionValue: string;
  actionValueMax?: string;
  actionValueType: 'pct' | 'abs';
}

interface TriggerRule {
  groupLogic: 'AND' | 'OR';
  conditionGroups: TriggerConditionGroup[];
  actions: TriggerAction[];
}

const emptyCondition = (): TriggerCondition => ({
  metric: 'CPA', operator: '大于', value: '150', valueMax: '', valueRef: 'target_pct',
});

const emptyGroup = (): TriggerConditionGroup => ({
  logic: 'AND', conditions: [emptyCondition()],
});

const emptyAction = (): TriggerAction => ({
  action: '', actionCountryFilter: '', actionValue: '', actionValueMax: '', actionValueType: 'pct',
});

const emptyRule = (): TriggerRule => ({
  groupLogic: 'AND', conditionGroups: [emptyGroup()],
  actions: [emptyAction()],
});

interface EntryForm {
  title: string;
  category: 'bid' | 'creative' | 'audience' | 'market' | 'keyword' | 'product' | 'monitor';
  description: string;
  triggerRules: TriggerRule[];
  diagnostic_tags: string[];
  counter_conditions: string[];
  platform: string;
  market: string;
  priority: 'high' | 'medium' | 'low';
  activation_mode: 'immediate' | 'gradual' | 'record_only';
  object_level: string;      // 适用层级
  object_account_ids: string; // 适用广告账户
  object_tag: string;        // 适用明细类型（保留兼容旧数据）
  campaign_status: string;
  adset_status: string;
  ad_status: string;
  scope_tag: string;         // 适用广告产品
  objective_type: string;    // 适用广告目标
  tag_new_old: string;           // 新老客/拉新
  tag_bid_strategy: string;      // 竞价策略
  tag_creative_type: string;     // 素材类型
  tag_funnel: string;            // 适用漏斗
  tag_series_type: string;       // 广告系列类型
  tag_attribution: string;       // 归因模型
  tag_event: string;             // 营销事件
  tag_product_category: string;  // 产品品类
  tag_material_size: string;     // 素材尺寸
  tag_landing_page: string;      // 广告落地页
  tag_audience_type: string;     // 受众细分（V站）
  tag_extra: Record<string, string>;
  expected_impact: string;       // 预期效果
}

const emptyForm = (): EntryForm => ({
  title: '', category: 'bid', description: '',
  triggerRules: [emptyRule()],
  diagnostic_tags: [],
  counter_conditions: [],
  platform: 'all', market: 'all',
  priority: 'medium', activation_mode: 'immediate',
  object_level: '', object_account_ids: '', object_tag: '', campaign_status: '', adset_status: '', ad_status: '', scope_tag: '', objective_type: '',
  tag_new_old: '', tag_bid_strategy: '', tag_creative_type: '',
  tag_funnel: '', tag_series_type: '', tag_attribution: '', tag_event: '',
  tag_product_category: '', tag_material_size: '', tag_landing_page: '', tag_audience_type: '',
  tag_extra: {},
  expected_impact: '',
});

type FlatTagDefinition = TagDefinition & {
  treeOptions: TagOption[];
  flatOptions: { value: string; label: string }[];
};

type FormKind = 'add' | 'edit';

type CustomWindowTarget = {
  formKind: FormKind;
  ruleIndex: number;
  groupIndex: number;
  conditionIndex: number;
};

const csvToArray = (value?: string) => value ? value.split(',').map(v => v.trim()).filter(Boolean) : [];
const arrayToCsv = (value: string[]) => value.filter(Boolean).join(',');

const DIAGNOSTIC_TAG_OPTIONS = [
  { value: 'roas_drop', label: 'ROAS下跌' },
  { value: 'cpa_up', label: 'CPA上升' },
  { value: 'cvr_drop', label: 'CVR下滑' },
  { value: 'ctr_drop', label: 'CTR下跌' },
  { value: 'ctr_stable', label: 'CTR持平' },
  { value: 'spend_up', label: '花费上升' },
  { value: 'purchase_drop', label: '购买下降' },
  { value: 'creative_fatigue', label: '素材疲劳' },
  { value: 'sample_too_small', label: '样本过小' },
  { value: 'tracking_issue', label: '追踪异常' },
  { value: 'inventory_risk', label: '库存风险' },
  { value: 'landing_page_issue', label: '落地页异常' },
  { value: 'attribution_delay', label: '归因延迟' },
  { value: 'bid_limited', label: '出价受限' },
  { value: 'budget_limited', label: '预算受限' },
];

const COUNTER_CONDITION_OPTIONS = [
  { value: 'sample_too_small', label: '样本过小' },
  { value: 'tracking_issue', label: '追踪异常' },
  { value: 'inventory_out_of_stock', label: '库存断货' },
  { value: 'new_campaign_learning_phase', label: '新计划学习期' },
  { value: 'attribution_delay', label: '归因延迟' },
  { value: 'promotion_changed', label: '活动刚调整' },
  { value: 'landing_page_changed', label: '落地页刚调整' },
];

const TRIGGER_METRIC_OPTIONS = [
  'CPA',
  'ROI',
  'ROAS',
  'Frequency',
  'ATC',
  '花费',
  '消耗占比',
  'CTR',
  'CPC',
  'CPM',
  '转化数',
  '购物次数',
  '广告条数',
  '单均价',
  '客单价',
  '单次加购成本',
  '单次注册成本',
  '展示次数份额',
  '落地页单次浏览费用',
  '1CTA-CPA',
  '1CTA-ROI',
  '商品状态',
].map(metric => ({ value: metric, label: metric }));

const V_ONLY_TRIGGER_METRIC_OPTIONS = [
  { value: '预算受限', label: '预算受限' },
];

const OBJECT_STATUS_OPTIONS = [
  { value: '投放中', label: '投放中' },
  { value: '已关闭', label: '已关闭' },
];

const parseTagExtra = (raw?: string): Record<string, string> => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.fromEntries(
        Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string')
      );
    }
  } catch {}
  return {};
};

const serializeTagExtra = (tagExtra: Record<string, string>) => {
  const filtered = Object.fromEntries(Object.entries(tagExtra).filter(([, value]) => value && value.trim()));
  return Object.keys(filtered).length ? JSON.stringify(filtered) : '';
};

const sanitizeExpectedImpact = (value?: string) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed.startsWith('{')) return value;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const keys = Object.keys(parsed);
      const looksLikeApplicableTags = keys.length > 0 && keys.every(key => ['campaign', 'adset', 'ad', 'account'].includes(key));
      if (looksLikeApplicableTags) {
        return '';
      }
    }
  } catch {}
  return value;
};

const metricNameMap: Record<string, string> = {
  CPA: 'cpa',
  ROI: 'roas',
  ROAS: 'roas',
  Frequency: 'frequency',
  ATC: 'add_to_cart',
  CTR: 'ctr',
  CPC: 'cpc',
  CPM: 'cpm',
  花费: 'spend',
  消耗占比: 'spend_share',
  转化数: 'conversion',
  购物次数: 'purchase_count',
  广告条数: 'ad_count',
  单均价: 'aov',
  客单价: 'aov',
  单次加购成本: 'add_to_cart_cost',
  单次注册成本: 'registration_cost',
  展示次数份额: 'impression_share',
  '1CTA-CPA': 'cta_cpa',
  '1CTA-ROI': 'cta_roi',
  落地页单次浏览费用: 'landing_page_view_cost',
  预算受限: 'budget_limited',
};

const parseNumericValue = (value?: string) => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const mapOperator = (condition: TriggerCondition): { operator: string; value: number; value_max?: number } | null => {
  const rawValue = parseNumericValue(condition.value);
  if (rawValue === undefined) return null;
  const rawMax = parseNumericValue(condition.valueMax);
  const op = condition.operator;

  if (condition.valueRef === 'abs') {
    if (op === '大于' || op === '>') return { operator: 'value_gt', value: rawValue };
    if (op === '大于等于' || op === '>=') return { operator: 'value_gte', value: rawValue };
    if (op === '小于' || op === '<') return { operator: 'value_lt', value: rawValue };
    if (op === '小于等于' || op === '<=') return { operator: 'value_lte', value: rawValue };
    if (op === '等于' || op === '=') return { operator: 'eq', value: rawValue };
    if ((op === '介于' || op === '不介于') && rawMax !== undefined) {
      return { operator: op === '介于' ? 'between' : 'not_between', value: rawValue, value_max: rawMax };
    }
    return null;
  }

  if (condition.valueRef === 'target_pct' || condition.valueRef === 'avg_pct' || condition.valueRef === 'market_avg_pct' || condition.valueRef === 'account_avg_pct') {
    const pctDelta = rawValue - 100;
    if (op === '大于' || op === '>' || op === '大于等于' || op === '>=') {
      return { operator: 'increase_pct_gte', value: Math.abs(pctDelta) };
    }
    if (op === '小于' || op === '<' || op === '小于等于' || op === '<=') {
      return { operator: 'decrease_pct_gte', value: Math.abs(pctDelta) };
    }
    if ((op === '介于' || op === '不介于') && rawMax !== undefined) {
      return { operator: op === '介于' ? 'between' : 'not_between', value: rawValue, value_max: rawMax };
    }
  }

  return null;
};

const buildMetricConditions = (rules: TriggerRule[]): MetricCondition[] =>
  rules
    .flatMap(rule => rule.conditionGroups || [])
    .flatMap(group => group.conditions || [])
    .map((condition): MetricCondition | null => {
      if (condition.metric === '预算受限') {
        const result: MetricCondition = {
          metric: 'budget_limited',
          operator: 'eq',
          value: 1,
        };
        if (condition.window && condition.window !== '不限') {
          result.window = condition.window;
        }
        return result;
      }
      const metric = metricNameMap[condition.metric] || condition.metric.toLowerCase();
      const mapped = mapOperator(condition);
      if (!mapped) return null;
      const result: MetricCondition = {
        metric,
        operator: mapped.operator,
        value: mapped.value,
      };
      if (mapped.value_max !== undefined) {
        result.value_max = mapped.value_max;
      }
      if (condition.window && condition.window !== '不限') {
        result.window = condition.window;
      }
      if (condition.valueRef !== 'abs') {
        result.reference = condition.valueRef;
      }
      return result;
    })
    .filter((condition): condition is MetricCondition => condition !== null);

const flattenTagOptions = (options: TagOption[], parentLabel?: string): { value: string; label: string }[] => {
  const flattened: { value: string; label: string }[] = [];
  const seen = new Set<string>();

  const visit = (items: TagOption[], parent?: string) => {
    for (const option of items) {
      const label = parent ? `${parent} / ${option.label}` : option.label;
      const key = `${option.value}@@${label}`;
      if (!seen.has(key)) {
        flattened.push({ value: option.value, label });
        seen.add(key);
      }
      if (option.children?.length) {
        visit(option.children, option.label);
      }
    }
  };

  visit(options, parentLabel);
  return flattened;
};

const uniquifyHierarchicalOptionValues = (options: TagOption[], parentPath = ''): TagOption[] => {
  const leafCounts = new Map<string, number>();

  const countLeaves = (items: TagOption[]) => {
    for (const item of items) {
      if (item.children?.length) {
        countLeaves(item.children);
      } else {
        leafCounts.set(item.value, (leafCounts.get(item.value) || 0) + 1);
      }
    }
  };

  countLeaves(options);

  const visit = (items: TagOption[], path: string): TagOption[] =>
    items.map((item) => {
      const currentPath = path ? `${path}__${item.value}` : item.value;
      if (item.children?.length) {
        return {
          ...item,
          children: visit(item.children, currentPath),
        };
      }
      if ((leafCounts.get(item.value) || 0) <= 1) {
        return item;
      }
      return {
        ...item,
        value: currentPath,
      };
    });

  return visit(options, parentPath);
};

const mergeTagOptionsTree = (base: TagOption[], incoming: TagOption[]) => {
  const merged = [...base];
  for (const next of incoming) {
    const existing = merged.find(item => item.value === next.value && item.label === next.label);
    if (!existing) {
      merged.push({
        ...next,
        children: next.children ? mergeTagOptionsTree([], [...next.children]) : undefined,
      });
      continue;
    }
    if (next.children?.length) {
      existing.children = mergeTagOptionsTree(existing.children ? [...existing.children] : [], [...next.children]);
    }
  }
  return merged;
};

const DEFAULT_WINDOW_OPTIONS = [
  { value: '不限', label: '不限' },
  { value: '当天', label: '今天' },
  { value: '当月', label: '本月' },
  { value: '近2天', label: '近2天' },
  { value: '近3天', label: '近3天' },
  { value: '近5天', label: '近5天' },
  { value: '近7天', label: '近7天' },
  { value: '近14天', label: '近14天' },
  { value: '__custom__', label: '自定义近N天' },
];

const buildWindowOptions = (currentValue?: string) => {
  const options = [...DEFAULT_WINDOW_OPTIONS];
  if (
    currentValue &&
    currentValue !== '不限' &&
    !options.some(option => option.value === currentValue)
  ) {
    options.splice(options.length - 1, 0, { value: currentValue, label: currentValue });
  }
  return options;
};

const BUDGET_RANGE_ACTIONS = new Set(['增加预算', '降低预算']);
const Z_NEW_CREATIVE_REMINDER_ACTIONS = ['停止测试', '可以二测', '关闭广告并二测', '正常投放'];
const NO_VALUE_ACTIONS = new Set([
  '发送通知',
  '暂停投放',
  '触发熔断',
  '关闭广告',
  '关闭素材',
  '开启广告',
  ...Z_NEW_CREATIVE_REMINDER_ACTIONS,
]);
const COUNTRY_FILTER_OPTIONS = [
  { value: 'qualified', label: '达标国家' },
  { value: 'unqualified', label: '不达标国家' },
  { value: 'placement_current', label: '达标版位' },
  { value: 'placement_unqualified', label: '不达标版位' },
];

const normalizeActionLabel = (value?: string) => {
  if (!value) return '';
  if (value === '提价') return '提高出价';
  if (value === '降价') return '降低出价';
  if (value === '扩大预算') return '增加预算';
  if (value === '缩减预算') return '降低预算';
  return value;
};

const normalizeTriggerAction = (action?: Partial<TriggerAction>): TriggerAction => ({
  action: normalizeActionLabel(action?.action || ''),
  actionCountryFilter: action?.actionCountryFilter || '',
  actionValue: action?.actionValue || '',
  actionValueMax: action?.actionValueMax || '',
  actionValueType: action?.actionValueType || 'pct',
});

const sortDynamicDefinitions = (definitions: FlatTagDefinition[]) =>
  [...definitions].sort((a, b) => {
    if ((a.display_order ?? 0) !== (b.display_order ?? 0)) return (a.display_order ?? 0) - (b.display_order ?? 0);
    if ((a.sort_order ?? 0) !== (b.sort_order ?? 0)) return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    return a.label.localeCompare(b.label, 'zh-CN');
  });

const normalizeTagDefinition = (definition: TagDefinition, station: 'z' | 'v'): TagDefinition => {
  if (
    station === 'v' &&
    definition.channel === 'google' &&
    definition.level === 'campaign' &&
    definition.key === 'customer_type'
  ) {
    return {
      ...definition,
      key: 'campaign_type',
    };
  }
  return definition;
};

const mergeFlatTagDefinitions = (definitions: FlatTagDefinition[]) => {
  const merged = new Map<string, FlatTagDefinition>();

  for (const def of definitions) {
    const existing = merged.get(def.key);
    if (!existing) {
      merged.set(def.key, {
        ...def,
        treeOptions: [...def.treeOptions],
        flatOptions: [...def.flatOptions],
      });
      continue;
    }

    const seen = new Set(existing.flatOptions.map(opt => `${opt.value}@@${opt.label}`));
    for (const option of def.flatOptions) {
      const optionKey = `${option.value}@@${option.label}`;
      if (!seen.has(optionKey)) {
        existing.flatOptions.push(option);
        seen.add(optionKey);
      }
    }

    existing.treeOptions = mergeTagOptionsTree(existing.treeOptions, def.treeOptions);

    if ((def.display_order ?? 0) < (existing.display_order ?? 0)) {
      existing.display_order = def.display_order;
    }

    if ((def.sort_order ?? 0) < (existing.sort_order ?? 0)) {
      existing.sort_order = def.sort_order;
    }
  }

  return Array.from(merged.values());
};

const TAG_KEY_ALIASES: Record<string, Record<string, string[]>> = {
  campaign: {
    campaign_type: ['campaign_type', 'series_type'],
    series_type: ['campaign_type', 'series_type'],
  },
  adset: {
    customer_type: ['customer_type', 'new_old_client'],
    new_old_client: ['customer_type', 'new_old_client'],
    funnel_level: ['funnel_level', 'campaign_goal'],
    campaign_goal: ['funnel_level', 'campaign_goal'],
    funnel_detail: ['funnel_detail', 'campaign_goal_detail'],
    campaign_goal_detail: ['funnel_detail', 'campaign_goal_detail'],
    gender_tag: ['gender_tag', 'gender'],
    gender: ['gender_tag', 'gender'],
  },
  ad: {
    gender_tag: ['gender_tag', 'gender'],
    gender: ['gender_tag', 'gender'],
  },
};

const appendApplicableTagValues = (
  applicableTags: Record<string, Record<string, string[]>>,
  level: string | undefined,
  key: string,
  values: string[],
) => {
  if (!level || !key || !values.length) return;
  const aliases = TAG_KEY_ALIASES[level]?.[key] || [key];
  for (const alias of aliases) {
    if (!applicableTags[level]) applicableTags[level] = {};
    const existing = applicableTags[level][alias] || [];
    const merged = [...existing];
    for (const value of values) {
      if (value && !merged.includes(value)) merged.push(value);
    }
    if (merged.length) applicableTags[level][alias] = merged;
  }
};

const STRATEGY_KEYS = [
  'campaign_goal',
  'campaign_goal_detail',
  'new_old_client',
  'new_client_market',
  'old_client_type',
  'first_order_type',
  'series_type',
  'audience_segment',
  'bid_strategy',
  'attribution_model',
  'gender',
  'keyword_type',
  'customer_type',
  'targeting_type',
  'funnel',
  'campaign_objective',
  'objective',
  'campaign_type',
  'purpose',
  'exclude_purchased',
  'event',
  'campaign_event',
];

const CREATIVE_KEYS = [
  'creative_type',
  'creative_resource_type',
  'creative_resource_detail',
  'creative_detail',
  'creative_label',
  'creative_label_detail',
  'price_range',
  'new_old_product',
  'product_position',
  'landing_page',
  'ad_placement',
  'placement',
  'publish_month',
  'catalog_type',
  'material',
  'material_type',
  'product_type',
  'publish_time',
];

const PRODUCT_CHANNEL_KEYS = [
  'product_tag_price',
  'product_new_time',
  'tag_price',
  'product_group',
  'product_edition',
  'product_style',
  'channel_performance',
  'device',
  'publish_time',
];

const V_COUNTRY_OPTIONS = [
  { value: 'AD', label: '安道尔 (AD)' },
  { value: 'AE', label: '阿拉伯联合酋长国 (AE)' },
  { value: 'AF', label: '阿富汗 (AF)' },
  { value: 'AG', label: '安提瓜和巴布达 (AG)' },
  { value: 'AI', label: '安圭拉 (AI)' },
  { value: 'AL', label: '阿尔巴尼亚 (AL)' },
  { value: 'AM', label: '亚美尼亚 (AM)' },
  { value: 'AN', label: '荷属安的列斯 (AN)' },
  { value: 'AO', label: '安哥拉 (AO)' },
  { value: 'AQ', label: '南极洲 (AQ)' },
  { value: 'AR', label: '阿根廷 (AR)' },
  { value: 'AS', label: '美属萨摩亚 (AS)' },
  { value: 'AT', label: '奥地利 (AT)' },
  { value: 'AU', label: '澳大利亚 (AU)' },
  { value: 'AW', label: '阿鲁巴 (AW)' },
  { value: 'AX', label: '奥兰群岛 (AX)' },
  { value: 'AZ', label: '阿塞拜疆 (AZ)' },
  { value: 'BA', label: '波斯尼亚和黑塞哥维那 (BA)' },
  { value: 'BB', label: '巴巴多斯 (BB)' },
  { value: 'BD', label: '孟加拉国 (BD)' },
  { value: 'BE', label: '比利时 (BE)' },
  { value: 'BF', label: '布基纳法索 (BF)' },
  { value: 'BG', label: '保加利亚 (BG)' },
  { value: 'BH', label: '巴林 (BH)' },
  { value: 'BI', label: '布隆迪 (BI)' },
  { value: 'BJ', label: '贝宁 (BJ)' },
  { value: 'BL', label: '圣巴泰勒米 (BL)' },
  { value: 'BM', label: '百慕大 (BM)' },
  { value: 'BN', label: '文莱 (BN)' },
  { value: 'BO', label: '玻利维亚 (BO)' },
  { value: 'BQ', label: '博内尔、圣尤斯特歇斯和沙巴 (BQ)' },
  { value: 'BR', label: '巴西 (BR)' },
  { value: 'BS', label: '巴哈马 (BS)' },
  { value: 'BT', label: '不丹 (BT)' },
  { value: 'BV', label: '布韦岛 (BV)' },
  { value: 'BW', label: '博茨瓦纳 (BW)' },
  { value: 'BY', label: '白俄罗斯 (BY)' },
  { value: 'BZ', label: '伯利兹 (BZ)' },
  { value: 'CA', label: '加拿大 (CA)' },
  { value: 'CC', label: '科科斯（基林）群岛 (CC)' },
  { value: 'CD', label: '刚果民主共和国 (CD)' },
  { value: 'CF', label: '中非共和国 (CF)' },
  { value: 'CG', label: '刚果共和国 (CG)' },
  { value: 'CH', label: '瑞士 (CH)' },
  { value: 'CI', label: '科特迪瓦 (CI)' },
  { value: 'CK', label: '库克群岛 (CK)' },
  { value: 'CL', label: '智利 (CL)' },
  { value: 'CM', label: '喀麦隆 (CM)' },
  { value: 'CN', label: '中国 (CN)' },
  { value: 'CO', label: '哥伦比亚 (CO)' },
  { value: 'CR', label: '哥斯达黎加 (CR)' },
  { value: 'CU', label: '古巴 (CU)' },
  { value: 'CV', label: '佛得角 (CV)' },
  { value: 'CW', label: '库拉索 (CW)' },
  { value: 'CX', label: '圣诞岛 (CX)' },
  { value: 'CY', label: '塞浦路斯 (CY)' },
  { value: 'CZ', label: '捷克 (CZ)' },
  { value: 'DE', label: '德国 (DE)' },
  { value: 'DJ', label: '吉布提 (DJ)' },
  { value: 'DK', label: '丹麦 (DK)' },
  { value: 'DM', label: '多米尼克 (DM)' },
  { value: 'DO', label: '多米尼加共和国 (DO)' },
  { value: 'DZ', label: '阿尔及利亚 (DZ)' },
  { value: 'EC', label: '厄瓜多尔 (EC)' },
  { value: 'EE', label: '爱沙尼亚 (EE)' },
  { value: 'EG', label: '埃及 (EG)' },
  { value: 'EH', label: '西撒哈拉 (EH)' },
  { value: 'ER', label: '厄立特里亚 (ER)' },
  { value: 'ES', label: '西班牙 (ES)' },
  { value: 'ET', label: '埃塞俄比亚 (ET)' },
  { value: 'FI', label: '芬兰 (FI)' },
  { value: 'FJ', label: '斐济 (FJ)' },
  { value: 'FK', label: '福克兰群岛 (FK)' },
  { value: 'FM', label: '密克罗尼西亚联邦 (FM)' },
  { value: 'FO', label: '法罗群岛 (FO)' },
  { value: 'FR', label: '法国 (FR)' },
  { value: 'GA', label: '加蓬 (GA)' },
  { value: 'GB', label: '英国 (GB)' },
  { value: 'GD', label: '格林纳达 (GD)' },
  { value: 'GE', label: '格鲁吉亚 (GE)' },
  { value: 'GF', label: '法属圭亚那 (GF)' },
  { value: 'GG', label: '根西岛 (GG)' },
  { value: 'GH', label: '加纳 (GH)' },
  { value: 'GI', label: '直布罗陀 (GI)' },
  { value: 'GL', label: '格陵兰 (GL)' },
  { value: 'GM', label: '冈比亚 (GM)' },
  { value: 'GN', label: '几内亚 (GN)' },
  { value: 'GP', label: '瓜德罗普 (GP)' },
  { value: 'GQ', label: '赤道几内亚 (GQ)' },
  { value: 'GR', label: '希腊 (GR)' },
  { value: 'GS', label: '南乔治亚岛和南桑威奇群岛 (GS)' },
  { value: 'GT', label: '危地马拉 (GT)' },
  { value: 'GU', label: '关岛 (GU)' },
  { value: 'GW', label: '几内亚比绍 (GW)' },
  { value: 'GY', label: '圭亚那 (GY)' },
  { value: 'HK', label: '香港 (HK)' },
  { value: 'HM', label: '赫德岛和麦克唐纳群岛 (HM)' },
  { value: 'HN', label: '洪都拉斯 (HN)' },
  { value: 'HR', label: '克罗地亚 (HR)' },
  { value: 'HT', label: '海地 (HT)' },
  { value: 'HU', label: '匈牙利 (HU)' },
  { value: 'ID', label: '印度尼西亚 (ID)' },
  { value: 'IE', label: '爱尔兰 (IE)' },
  { value: 'IL', label: '以色列 (IL)' },
  { value: 'IM', label: '马恩岛 (IM)' },
  { value: 'IN', label: '印度 (IN)' },
  { value: 'IO', label: '英属印度洋领地 (IO)' },
  { value: 'IQ', label: '伊拉克 (IQ)' },
  { value: 'IR', label: '伊朗 (IR)' },
  { value: 'IS', label: '冰岛 (IS)' },
  { value: 'IT', label: '意大利 (IT)' },
  { value: 'JE', label: '泽西 (JE)' },
  { value: 'JM', label: '牙买加 (JM)' },
  { value: 'JO', label: '约旦 (JO)' },
  { value: 'JP', label: '日本 (JP)' },
  { value: 'KE', label: '肯尼亚 (KE)' },
  { value: 'KG', label: '吉尔吉斯斯坦 (KG)' },
  { value: 'KH', label: '柬埔寨 (KH)' },
  { value: 'KI', label: '基里巴斯 (KI)' },
  { value: 'KM', label: '科摩罗 (KM)' },
  { value: 'KN', label: '圣基茨和尼维斯 (KN)' },
  { value: 'KP', label: '朝鲜 (KP)' },
  { value: 'KR', label: '韩国 (KR)' },
  { value: 'KW', label: '科威特 (KW)' },
  { value: 'KY', label: '开曼群岛 (KY)' },
  { value: 'KZ', label: '哈萨克斯坦 (KZ)' },
  { value: 'LA', label: '老挝 (LA)' },
  { value: 'LB', label: '黎巴嫩 (LB)' },
  { value: 'LC', label: '圣卢西亚 (LC)' },
  { value: 'LI', label: '列支敦士登 (LI)' },
  { value: 'LK', label: '斯里兰卡 (LK)' },
  { value: 'LR', label: '利比里亚 (LR)' },
  { value: 'LS', label: '莱索托 (LS)' },
  { value: 'LT', label: '立陶宛 (LT)' },
  { value: 'LU', label: '卢森堡 (LU)' },
  { value: 'LV', label: '拉脱维亚 (LV)' },
  { value: 'LY', label: '利比亚 (LY)' },
  { value: 'MA', label: '摩洛哥 (MA)' },
  { value: 'MC', label: '摩纳哥 (MC)' },
  { value: 'MD', label: '摩尔多瓦 (MD)' },
  { value: 'ME', label: '黑山 (ME)' },
  { value: 'MF', label: '法属圣马丁 (MF)' },
  { value: 'MG', label: '马达加斯加 (MG)' },
  { value: 'MH', label: '马绍尔群岛 (MH)' },
  { value: 'MK', label: '北马其顿共和国 (MK)' },
  { value: 'ML', label: '马里 (ML)' },
  { value: 'MM', label: '缅甸 (MM)' },
  { value: 'MN', label: '蒙古 (MN)' },
  { value: 'MO', label: '澳门 (MO)' },
  { value: 'MP', label: '北马里亚纳群岛 (MP)' },
  { value: 'MQ', label: '马提尼克 (MQ)' },
  { value: 'MR', label: '毛里塔尼亚 (MR)' },
  { value: 'MS', label: '蒙特塞拉特 (MS)' },
  { value: 'MT', label: '马耳他 (MT)' },
  { value: 'MU', label: '毛里求斯 (MU)' },
  { value: 'MV', label: '马尔代夫 (MV)' },
  { value: 'MW', label: '马拉维 (MW)' },
  { value: 'MX', label: '墨西哥 (MX)' },
  { value: 'MY', label: '马来西亚 (MY)' },
  { value: 'MZ', label: '莫桑比克 (MZ)' },
  { value: 'NA', label: '纳米比亚 (NA)' },
  { value: 'NC', label: '新喀里多尼亚 (NC)' },
  { value: 'NE', label: '尼日尔 (NE)' },
  { value: 'NF', label: '诺福克岛 (NF)' },
  { value: 'NG', label: '尼日利亚 (NG)' },
  { value: 'NI', label: '尼加拉瓜 (NI)' },
  { value: 'NL', label: '荷兰 (NL)' },
  { value: 'NO', label: '挪威 (NO)' },
  { value: 'NP', label: '尼泊尔 (NP)' },
  { value: 'NR', label: '瑙鲁 (NR)' },
  { value: 'NU', label: '纽埃 (NU)' },
  { value: 'NZ', label: '新西兰 (NZ)' },
  { value: 'OM', label: '阿曼 (OM)' },
  { value: 'PA', label: '巴拿马 (PA)' },
  { value: 'PE', label: '秘鲁 (PE)' },
  { value: 'PF', label: '法属波利尼西亚 (PF)' },
  { value: 'PG', label: '巴布亚新几内亚 (PG)' },
  { value: 'PH', label: '菲律宾 (PH)' },
  { value: 'PK', label: '巴基斯坦 (PK)' },
  { value: 'PL', label: '波兰 (PL)' },
  { value: 'PM', label: '圣皮埃尔和密克隆 (PM)' },
  { value: 'PN', label: '皮特凯恩群岛 (PN)' },
  { value: 'PR', label: '波多黎各 (PR)' },
  { value: 'PS', label: '巴勒斯坦 (PS)' },
  { value: 'PT', label: '葡萄牙 (PT)' },
  { value: 'PW', label: '帕劳 (PW)' },
  { value: 'PY', label: '巴拉圭 (PY)' },
  { value: 'QA', label: '卡塔尔 (QA)' },
  { value: 'RE', label: '留尼汪 (RE)' },
  { value: 'RO', label: '罗马尼亚 (RO)' },
  { value: 'RS', label: '塞尔维亚 (RS)' },
  { value: 'RU', label: '俄罗斯 (RU)' },
  { value: 'RW', label: '卢旺达 (RW)' },
  { value: 'SA', label: '沙特阿拉伯 (SA)' },
  { value: 'SB', label: '所罗门群岛 (SB)' },
  { value: 'SC', label: '塞舌尔 (SC)' },
  { value: 'SD', label: '苏丹 (SD)' },
  { value: 'SE', label: '瑞典 (SE)' },
  { value: 'SG', label: '新加坡 (SG)' },
  { value: 'SH', label: '圣赫勒拿 (SH)' },
  { value: 'SI', label: '斯洛文尼亚 (SI)' },
  { value: 'SJ', label: '斯瓦尔巴群岛和扬马延岛 (SJ)' },
  { value: 'SK', label: '斯洛伐克 (SK)' },
  { value: 'SL', label: '塞拉利昂 (SL)' },
  { value: 'SM', label: '圣马力诺 (SM)' },
  { value: 'SN', label: '塞内加尔 (SN)' },
  { value: 'SO', label: '索马里 (SO)' },
  { value: 'SR', label: '苏里南 (SR)' },
  { value: 'SS', label: '南苏丹 (SS)' },
  { value: 'ST', label: '圣多美和普林西比 (ST)' },
  { value: 'SV', label: '萨尔瓦多 (SV)' },
  { value: 'SX', label: '荷属圣马丁 (SX)' },
  { value: 'SY', label: '叙利亚 (SY)' },
  { value: 'SZ', label: '斯威士兰 (SZ)' },
  { value: 'TC', label: '特克斯和凯科斯群岛 (TC)' },
  { value: 'TD', label: '乍得 (TD)' },
  { value: 'TF', label: '法属南部领地 (TF)' },
  { value: 'TG', label: '多哥 (TG)' },
  { value: 'TH', label: '泰国 (TH)' },
  { value: 'TJ', label: '塔吉克斯坦 (TJ)' },
  { value: 'TK', label: '托克劳 (TK)' },
  { value: 'TL', label: '东帝汶 (TL)' },
  { value: 'TM', label: '土库曼斯坦 (TM)' },
  { value: 'TN', label: '突尼斯 (TN)' },
  { value: 'TO', label: '汤加 (TO)' },
  { value: 'TR', label: '土耳其 (TR)' },
  { value: 'TT', label: '特立尼达和多巴哥 (TT)' },
  { value: 'TV', label: '图瓦卢 (TV)' },
  { value: 'TW', label: '台湾 (TW)' },
  { value: 'TZ', label: '坦桑尼亚 (TZ)' },
  { value: 'UA', label: '乌克兰 (UA)' },
  { value: 'UG', label: '乌干达 (UG)' },
  { value: 'UM', label: '美国本土外小岛屿 (UM)' },
  { value: 'US', label: '美国 (US)' },
  { value: 'UY', label: '乌拉圭 (UY)' },
  { value: 'UZ', label: '乌兹别克斯坦 (UZ)' },
  { value: 'VA', label: '梵蒂冈 (VA)' },
  { value: 'VC', label: '圣文森特和格林纳丁斯 (VC)' },
  { value: 'VE', label: '委内瑞拉 (VE)' },
  { value: 'VG', label: '英属维尔京群岛 (VG)' },
  { value: 'VI', label: '美属维尔京群岛 (VI)' },
  { value: 'VN', label: '越南 (VN)' },
  { value: 'VU', label: '瓦努阿图 (VU)' },
  { value: 'WF', label: '瓦利斯和富图纳群岛 (WF)' },
  { value: 'WS', label: '萨摩亚 (WS)' },
  { value: 'XK', label: '科索沃 (XK)' },
  { value: 'YE', label: '也门 (YE)' },
  { value: 'YT', label: '马约特 (YT)' },
  { value: 'ZA', label: '南非 (ZA)' },
  { value: 'ZM', label: '赞比亚 (ZM)' },
  { value: 'ZW', label: '津巴布韦 (ZW)' },
];

const FIELD_OPTIONS = {
  // ── ZV 通用 ──────────────────────────────────────────────────────────────
  platform: [
    { value: 'all',      label: '全平台' },
    { value: 'tiktok',   label: 'TikTok' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'google',   label: 'Google' },
  ],
  market: [
    { value: 'all', label: '全部市场' },
    { value: 'US',  label: '美国 (US)' },
    { value: 'CA',  label: '加拿大 (CA)' },
    { value: 'DE',  label: '德国 (DE)' },
    { value: 'UK',  label: '英国 (UK)' },
    { value: 'FR',  label: '法国 (FR)' }
  ],
  // ── Z 站 ─────────────────────────────────────────────────────────────────
  object_level:    ['账户','广告系列','广告组','广告'].map(v => ({ value: v, label: v })),
  object_tag:      ['账户','新系列','老系列','新广告组','老广告组','当月新素材','当月老素材','素材类型'].map(v => ({ value: v, label: v })),
  scope_tag:       ['素材','广告'].map(v => ({ value: v, label: v })),
  objective_type:  [],
  // ── V 站 ─────────────────────────────────────────────────────────────────
  tag_funnel:           ['上层','中层','下层'].map(v => ({ value: v, label: v })),
  tag_new_old:          ['拉新','再营销'].map(v => ({ value: v, label: v })),
  tag_series_type:      ['fb','brand','demand','search','pmax','asc','daba','Google brand','Google pmax','Google search'].map(v => ({ value: v, label: v })),
  tag_attribution:      ['增量归因','标准归因'].map(v => ({ value: v, label: v })),
  tag_bid_strategy:     ['最大数量','最高价值'].map(v => ({ value: v, label: v })),
  tag_event:            ['是','否'].map(v => ({ value: v, label: v })),
  tag_product_category: ['太阳镜','平光镜'].map(v => ({ value: v, label: v })),
  tag_creative_type:    ['图片','视频','红人贴','feed广告'].map(v => ({ value: v, label: v })),
  tag_material_size:    ['全尺寸','横版','竖版','方版'].map(v => ({ value: v, label: v })),
  tag_landing_page:     ['商详页','列表页','专题页','首页'].map(v => ({ value: v, label: v })),
  tag_audience_type:    ['拉新','再营销','通用'].map(v => ({ value: v, label: v })),
};

const FIXED_FIELD_HELP: Record<string, string> = {
  适用平台: '用于限定这条经验适用于哪些广告投放平台。选择全平台时，表示这条经验可跨平台复用。',
  适用广告账户: '用于限定这条经验只适用于指定广告账户。通常在账户结构、预算策略或历史打法明显不同的场景下使用。',
  适用市场: '用于限定这条经验适用的投放国家或市场。不同市场的人群、价格带和素材偏好可能不同。',
  适用国家: '用于限定这条经验适用的投放国家。不同国家的人群、价格带和素材偏好可能不同。',
  适用层级: '用于说明这条经验主要作用在哪一层：账户、广告系列、广告组或广告。',
  适用明细: '用于补充说明适用对象的细分类别。其中：新系列/新广告组指开始投放后4天内，超过4天归为老系列/老广告组；新老素材按自然月划分，当月上线为当月新素材，非当月为当月老素材。',
};

export default function KnowledgeBank() {
  const { brandId, siteId, brand, hasPermission, adContext } = useAppStore();
  const station = brand === 'vooglam' ? 'v' : 'z';
  const userLevel = adContext?.data_level ?? 1;
  const isMobile = useIsMobile();
  const canViewFullKnowledge = userLevel >= 3;
  const canWriteKnowledge = userLevel >= 3 && hasPermission('data:knowledge:create');
  const canDeleteKnowledge = userLevel >= 3 && hasPermission('data:knowledge:delete');

  const [entries, setEntries]             = useState<KnowledgeEntry[]>([]);
  const [total, setTotal]                 = useState(0);
  const [loading, setLoading]             = useState(false);
  const [activeCat, setActiveCat]         = useState<'all' | 'bid' | 'creative' | 'audience' | 'market' | 'keyword' | 'product' | 'monitor'>('all');
  const [sourceFilter, setSourceFilter]   = useState<'all' | 'manual' | 'ai_learned'>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [search, setSearch]               = useState('');
  const [searchInput, setSearchInput]     = useState('');
  const [currentPage, setCurrentPage]     = useState(1);
  const [pageSize, setPageSize]           = useState(9);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null);
  const [showAddModal, setShowAddModal]   = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal]     = useState(false);
  const [showExportModal, setShowExportModal]       = useState(false);
  const [addForm, setAddForm]             = useState<EntryForm>(emptyForm());
  const [editForm, setEditForm]           = useState<EntryForm>(emptyForm());
  const [editingId, setEditingId]         = useState<string | null>(null);
  const [saving, setSaving]               = useState(false);
  const [deletingId, setDeletingId]       = useState<string | null>(null);
  const [toast, setToast]                 = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [exportFormat, setExportFormat]   = useState<'pdf' | 'excel' | 'csv'>('pdf');
  const [candidateEntries, setCandidateEntries] = useState<KnowledgeEntry[]>([]);
  const [allTagDefinitions, setAllTagDefinitions] = useState<FlatTagDefinition[]>([]);
  const [tagDefinitions, setTagDefinitions] = useState<FlatTagDefinition[]>([]);
  const [allPlatformAccounts, setAllPlatformAccounts] = useState<PlatformAccountSimple[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [customWindowTarget, setCustomWindowTarget] = useState<CustomWindowTarget | null>(null);
  const [customWindowDays, setCustomWindowDays] = useState('');
  const addAutoDescriptionRef = useRef('');
  const editAutoDescriptionRef = useRef('');
  const addDescriptionTouchedRef = useRef(false);
  const editDescriptionTouchedRef = useRef(false);
  const editDescriptionBaselineRef = useRef('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const platformOptions = station === 'v'
    ? FIELD_OPTIONS.platform.filter(option => option.value !== 'tiktok')
    : FIELD_OPTIONS.platform;
  const triggerMetricOptions = station === 'v'
    ? [...TRIGGER_METRIC_OPTIONS, ...V_ONLY_TRIGGER_METRIC_OPTIONS]
    : TRIGGER_METRIC_OPTIONS;
  const marketFieldLabel = '适用市场';
  const marketOptions = station === 'v'
    ? [{ value: 'all', label: '全部国家' }, ...V_COUNTRY_OPTIONS]
    : FIELD_OPTIONS.market;
  const objectLevelOptions = FIELD_OPTIONS.object_level;
  const marketLabelMap = new Map(marketOptions.map(option => [option.value, option.label]));

  const platformToChannel = (p: string) =>
    p === 'google' ? 'google' : p === 'tiktok' ? 'tiktok' : p === 'facebook' ? 'fb' : '';

  const loadPlatformAccounts = useCallback(async () => {
    if (!brandId) {
      setAllPlatformAccounts([]);
      return;
    }
    setAccountsLoading(true);
    try {
      const res = await authAccountsApi.listPlatformAccounts({
        brand_id: brandId,
        site_id: siteId || undefined,
        page: 1,
        size: 500,
      });
      setAllPlatformAccounts(res.data || []);
    } catch (e) {
      console.error('loadPlatformAccounts error:', e);
      setAllPlatformAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [brandId, siteId]);

  const getAccountOptions = useCallback((platform: string) => {
    const normalized = platform === 'facebook' || platform === 'google' || platform === 'tiktok' ? platform : 'all';
    const source = normalized === 'all'
      ? allPlatformAccounts
      : allPlatformAccounts.filter(item => item.platform === normalized);
    const seen = new Set<string>();
    return source
      .filter(item => {
        const accountID = String(item.account_id || '').trim();
        const compositeID = `${item.platform}::${accountID}`;
        if (!accountID || seen.has(compositeID)) return false;
        seen.add(compositeID);
        return true;
      })
      .map(item => ({
        value: `${item.platform}::${item.account_id}`,
        label: normalized === 'all'
          ? `[${platformIcons[item.platform] || item.platform}] ${item.account_name || item.account_id} (${item.account_id})`
          : `${item.account_name || item.account_id} (${item.account_id})`,
      }));
  }, [allPlatformAccounts]);

  const normalizeAccountSelection = useCallback((platform: string, objectLevel: string, selectedIDs: string) => {
    if (!csvToArray(objectLevel).includes('账户')) return '';
    const allowed = new Set(getAccountOptions(platform).map(option => option.value));
    return arrayToCsv(csvToArray(selectedIDs).filter(id => allowed.has(id)));
  }, [getAccountOptions]);

  const getActionOptions = useCallback((form: EntryForm) => {
    const objectLevels = csvToArray(form.object_level);
    const objectTags = csvToArray(form.object_tag);
    const isZNewCreativeAdRule = station === 'z'
      && objectLevels.includes('广告')
      && objectTags.some(tag => tag.includes('新素材'));

    const base = ['发送通知','降低出价','提高出价','增加预算','降低预算','暂停投放','关闭广告','关闭素材','开启广告','触发熔断'];
    return isZNewCreativeAdRule ? [...base, ...Z_NEW_CREATIVE_REMINDER_ACTIONS] : base;
  }, [station]);

  const normalizeClientDependencyTagExtra = useCallback((tagExtra: Record<string, string>) => {
    const next = { ...tagExtra };
    const newOldDef = allTagDefinitions.find(item => item.key === 'new_old_client');
    const newMarketDef = allTagDefinitions.find(item => item.key === 'new_client_market');
    const oldClientDef = allTagDefinitions.find(item => item.key === 'old_client_type');
    const customerTypeDef = allTagDefinitions.find(item => item.key === 'customer_type');
    const objectiveDef = allTagDefinitions.find(item => item.key === 'objective')
      || allTagDefinitions.find(item => item.key === 'purpose');

    if (customerTypeDef && objectiveDef) {
      const selectedObjectives = csvToArray(next[objectiveDef.key]);
      if (!selectedObjectives.includes('再营销')) {
        delete next[customerTypeDef.key];
      }
    }

    if (!newOldDef) return next;

    const selected = csvToArray(next[newOldDef.key]);
    if (!selected.includes('新客') && newMarketDef) {
      delete next[newMarketDef.key];
    }
    if (!selected.includes('老客') && oldClientDef) {
      delete next[oldClientDef.key];
    }
    return next;
  }, [allTagDefinitions]);

  const getAccountLabels = useCallback((platform: string, selectedIDs: string) => {
    const optionMap = new Map(getAccountOptions(platform).map(option => [option.value, option.label]));
    return csvToArray(selectedIDs).map(id => optionMap.get(id) || id);
  }, [getAccountOptions]);

  const getMarketLabel = useCallback((value?: string) => {
    if (!value || value === 'all') return station === 'v' ? '全部国家' : '全部市场';
    return marketLabelMap.get(value) || value;
  }, [marketLabelMap, station]);

  const getApplicableStatus = useCallback((entry: KnowledgeEntry, level: 'campaign' | 'adset' | 'ad') => {
    const levelTags = entry.applicable_tags?.[level] || {};
    return (levelTags.status || levelTags.object_status || [])[0] || '';
  }, []);

  const loadAllTagConfig = useCallback(async () => {
    try {
      const res = await adTagsApi.getConfig(station);
      const defs: FlatTagDefinition[] = (res.data || []).map(rawDef => {
        const def = normalizeTagDefinition(rawDef, station);
        return {
        ...def,
        treeOptions: uniquifyHierarchicalOptionValues([...(def.options || [])]),
        flatOptions: flattenTagOptions(uniquifyHierarchicalOptionValues([...(def.options || [])])),
      }});
      setAllTagDefinitions(defs);
    } catch (e) {
      console.error('loadTagConfig error:', e);
      setAllTagDefinitions([]);
    }
  }, [station]);

  const applyTagDefinitions = useCallback((platform: string) => {
    const channel = platformToChannel(platform);
    if (!channel) {
      setTagDefinitions(
        sortDynamicDefinitions(
          mergeFlatTagDefinitions(allTagDefinitions)
        )
      );
      return;
    }
    setTagDefinitions(allTagDefinitions.filter(def => (def.channel || '') === channel));
  }, [allTagDefinitions]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const openCustomWindowModal = (formKind: FormKind, ruleIndex: number, groupIndex: number, conditionIndex: number, currentWindow?: string) => {
    const matched = currentWindow?.match(/^近(\d+)天$/);
    setCustomWindowDays(matched?.[1] || '');
    setCustomWindowTarget({ formKind, ruleIndex, groupIndex, conditionIndex });
  };

  const applyCustomWindow = () => {
    const days = customWindowDays.trim();
    if (!/^\d+$/.test(days) || Number(days) <= 0) {
      showToast('error', '请输入大于 0 的天数');
      return;
    }
    if (!customWindowTarget) return;

    const nextWindow = `近${Number(days)}天`;
    const updater = (prev: EntryForm): EntryForm => {
      const rules = [...prev.triggerRules];
      const groups = [...rules[customWindowTarget.ruleIndex].conditionGroups];
      const conditions = [...groups[customWindowTarget.groupIndex].conditions];
      conditions[customWindowTarget.conditionIndex] = {
        ...conditions[customWindowTarget.conditionIndex],
        window: nextWindow,
      };
      groups[customWindowTarget.groupIndex] = { ...groups[customWindowTarget.groupIndex], conditions };
      rules[customWindowTarget.ruleIndex] = { ...rules[customWindowTarget.ruleIndex], conditionGroups: groups };
      return { ...prev, triggerRules: rules };
    };

    if (customWindowTarget.formKind === 'add') {
      setAddForm(updater);
    } else {
      setEditForm(updater);
    }

    setCustomWindowTarget(null);
    setCustomWindowDays('');
  };

  const maskText = (value?: string, fallback = '****') => {
    const text = value?.trim() || '';
    if (!text) return fallback;
    if (text.length <= 2) return '****';
    return `${text.slice(0, 1)}${'*'.repeat(Math.min(6, Math.max(3, text.length - 1)))}`;
  };

  const displayText = (value?: string, fallback = '****') => (
    canViewFullKnowledge ? (value?.trim() || fallback) : maskText(value, fallback)
  );

  const displayUsageCount = (value?: number) => (
    canViewFullKnowledge ? `${value ?? 0}` : '***'
  );

  const fetchEntries = useCallback(async (page = currentPage) => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { brand_id: brandId, site_id: siteId, page, size: pageSize };
      if (activeCat !== 'all') params.category = activeCat;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (platformFilter !== 'all') params.platform = platformFilter;
      if (search) params.search = search;
      const res = await knowledgeApi.list(params as any);
      setEntries(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('Failed to fetch knowledge entries', e);
    } finally {
      setLoading(false);
    }
  }, [brandId, siteId, activeCat, sourceFilter, platformFilter, search, currentPage, pageSize]);

  const fetchCandidates = useCallback(async () => {
    try {
      const res = await knowledgeApi.list({ brand_id: brandId, site_id: siteId, status: 'candidate', size: 100 });
      setCandidateEntries(res.data || []);
    } catch (e) {
      console.error('Failed to fetch candidates', e);
    }
  }, [brandId, siteId]);

  useEffect(() => {
    setCurrentPage(1);
    fetchEntries(1);
  }, [brandId, siteId]);

  useEffect(() => {
    fetchEntries(currentPage);
  }, [activeCat, sourceFilter, platformFilter, search, currentPage]);

  useEffect(() => {
    fetchCandidates();
  }, [brandId, siteId]);

  useEffect(() => {
    loadAllTagConfig();
  }, [loadAllTagConfig]);

  useEffect(() => {
    loadPlatformAccounts();
  }, [loadPlatformAccounts]);

  useEffect(() => {
    if (showAddModal) applyTagDefinitions(addForm.platform);
  }, [addForm.platform, showAddModal, applyTagDefinitions]);

  useEffect(() => {
    if (showEditModal) applyTagDefinitions(editForm.platform);
  }, [editForm.platform, showEditModal, applyTagDefinitions]);

  useEffect(() => {
    if (!showAddModal) return;
    const normalized = normalizeAccountSelection(addForm.platform, addForm.object_level, addForm.object_account_ids);
    if (normalized !== addForm.object_account_ids) {
      setAddForm(prev => ({ ...prev, object_account_ids: normalized }));
    }
  }, [showAddModal, addForm.platform, addForm.object_level, addForm.object_account_ids, normalizeAccountSelection]);

  useEffect(() => {
    if (!showEditModal) return;
    const normalized = normalizeAccountSelection(editForm.platform, editForm.object_level, editForm.object_account_ids);
    if (normalized !== editForm.object_account_ids) {
      setEditForm(prev => ({ ...prev, object_account_ids: normalized }));
    }
  }, [showEditModal, editForm.platform, editForm.object_level, editForm.object_account_ids, normalizeAccountSelection]);

  useEffect(() => {
    if (!showAddModal) return;
    const normalized = normalizeClientDependencyTagExtra(addForm.tag_extra);
    if (JSON.stringify(normalized) !== JSON.stringify(addForm.tag_extra)) {
      setAddForm(prev => ({ ...prev, tag_extra: normalized }));
    }
  }, [showAddModal, addForm.tag_extra, normalizeClientDependencyTagExtra]);

  useEffect(() => {
    if (!showEditModal) return;
    const normalized = normalizeClientDependencyTagExtra(editForm.tag_extra);
    if (JSON.stringify(normalized) !== JSON.stringify(editForm.tag_extra)) {
      setEditForm(prev => ({ ...prev, tag_extra: normalized }));
    }
  }, [showEditModal, editForm.tag_extra, normalizeClientDependencyTagExtra]);

  useEffect(() => {
    if (station !== 'v') return;
    if (addForm.platform === 'tiktok') {
      setAddForm(prev => ({ ...prev, platform: 'all' }));
    }
    if (editForm.platform === 'tiktok') {
      setEditForm(prev => ({ ...prev, platform: 'all' }));
    }
  }, [station, addForm.platform, editForm.platform]);

  const handleSearchChange = (val: string) => {
    setSearchInput(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setCurrentPage(1);
    }, 350);
  };

  const handleCatChange = (cat: typeof activeCat) => {
    setActiveCat(cat);
    setCurrentPage(1);
  };

  const handleSourceChange = (src: typeof sourceFilter) => {
    setSourceFilter(src);
    setCurrentPage(1);
  };

  const handlePlatformChange = (platform: string) => {
    setPlatformFilter(platform);
    setCurrentPage(1);
  };

  const serializeTriggerRules = (rules: TriggerRule[]): string =>
    JSON.stringify(rules.filter(r => r.conditionGroups?.some(g => g.conditions?.length)));

  const parseTriggerRules = (raw: string): TriggerRule[] => {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // 兼容最旧格式 { metric, operator, targetPct, action, actionPct }
        if ('targetPct' in parsed[0]) {
            return (parsed as any[]).map(r => ({
              groupLogic: 'AND' as const,
              conditionGroups: [{ logic: 'AND' as const, conditions: [{ metric: r.metric || 'CPA', operator: r.operator || '大于', value: r.targetPct || '150', valueMax: '', valueRef: 'target_pct' as const }] }],
              actions: [normalizeTriggerAction({
                action: normalizeActionLabel(r.action) || '发送通知',
                actionCountryFilter: r.actionCountryFilter || '',
                actionValue: r.actionPct || '',
                actionValueType: 'pct',
              })],
            }));
          }
        // 兼容旧格式 { window, logic, conditions, ... }
        if ('conditions' in parsed[0] && !('conditionGroups' in parsed[0])) {
            return (parsed as any[]).map(r => ({
              groupLogic: 'AND' as const,
              conditionGroups: [{
                logic: (r.logic || 'AND') as 'AND' | 'OR',
                conditions: (r.conditions || []).map((c: any) => ({ ...c, valueMax: c.valueMax || '', window: r.window || undefined })),
              }],
              actions: [normalizeTriggerAction({
                action: r.action === '打标不达标'
                  ? '发送通知'
                  : normalizeActionLabel(r.action || ''),
                actionCountryFilter: r.action === '打标不达标'
                  ? 'unqualified'
                  : r.action === '仅暂停该系列在不达标国家的投放'
                    ? 'unqualified'
                    : (r.actionCountryFilter || ''),
                actionValue: r.actionValue || '',
                actionValueMax: r.actionValueMax || '',
                actionValueType: (r.actionValueType || 'pct') as 'pct' | 'abs',
              })],
            }));
          }
        return (parsed as any[]).map(rule => ({
          ...rule,
          actions: Array.isArray(rule.actions) && rule.actions.length > 0
            ? rule.actions.map((item: TriggerAction) => normalizeTriggerAction(item))
            : [normalizeTriggerAction({
                action: rule.action || '',
                actionCountryFilter: rule.actionCountryFilter || '',
                actionValue: rule.actionValue || '',
                actionValueMax: rule.actionValueMax || '',
                actionValueType: rule.actionValueType || 'pct',
              })],
        }));
      }
    } catch {}
    return [];
  };

  const renderTriggerRule = (r: TriggerRule) => {
    const groups = r.conditionGroups || [];
    const groupStrs = groups.map(g => {
      const conds = (g.conditions || []).map(c => {
        const win = c.window ? `[${c.window}]` : '';
        const formatVal = (raw: string) => c.valueRef === 'target_pct'
          ? `目标值的 ${raw}%`
          : c.valueRef === 'avg_pct'
            ? `大盘均值的 ${raw}%`
            : raw;
        const val = (c.operator === '介于' || c.operator === '不介于') && c.valueMax
          ? `${formatVal(c.value)} ~ ${formatVal(c.valueMax)}`
          : formatVal(c.value);
        return `${win}${c.metric} ${c.operator} ${val}`;
      }).join(g.logic === 'OR' ? ' 或 ' : ' 且 ');
      return groups.length > 1 ? `(${conds})` : conds;
      }).join(r.groupLogic === 'OR' ? ' 或 ' : ' 且 ');
      const act = (r.actions || [])
        .filter(item => item.action)
        .map(item => {
          const countryFilterLabel = (item.actionCountryFilter || '')
            .split(',')
            .filter(Boolean)
            .map(value => COUNTRY_FILTER_OPTIONS.find(option => option.value === value)?.label || value)
            .join('、');
          const actionUnit = item.actionValueType === 'pct' ? '%' : '';
          const actionRange = item.actionValue && item.actionValueMax
            ? `${item.actionValue}-${item.actionValueMax}${actionUnit}`
            : item.actionValue
              ? `${item.actionValue}${actionUnit}`
              : '';
          const actionBase = countryFilterLabel ? `${item.action}（作用范围：${countryFilterLabel}）` : item.action;
          return actionRange ? `${actionBase} ${actionRange}` : actionBase;
        })
        .join('；');
      return `当 ${groupStrs}，则 ${act}`;
    };

  const buildApplicableTags = useCallback((form: EntryForm): Record<string, Record<string, string[]>> => {
    const applicableTags: Record<string, Record<string, string[]>> = {};

    const appendTag = (level: string | undefined, key: string, values: string[]) => {
      appendApplicableTagValues(applicableTags, level, key, values);
    };

    const dynamicDefs = mergeFlatTagDefinitions(allTagDefinitions);
    for (const def of dynamicDefs) {
      const selected = csvToArray(form.tag_extra?.[def.key]);
      if (!selected.length) continue;
      appendTag(def.level, def.key, selected);
    }

    appendTag('campaign', 'campaign_type', csvToArray(form.tag_series_type));
    appendTag('campaign', 'status', form.campaign_status ? [form.campaign_status] : []);
    appendTag('adset', 'status', form.adset_status ? [form.adset_status] : []);
    appendTag('ad', 'status', form.ad_status ? [form.ad_status] : []);
    appendTag('adset', 'bid_strategy', csvToArray(form.tag_bid_strategy));
    appendTag('ad', 'creative_type', csvToArray(form.tag_creative_type));
    appendTag('ad', 'landing_page', csvToArray(form.tag_landing_page));
    appendTag('adset', 'audience_type', csvToArray(form.tag_audience_type));
    appendTag('adset', 'funnel_level', csvToArray(form.tag_funnel));
    appendTag('adset', 'customer_type', csvToArray(form.tag_new_old));
    appendTag('adset', 'attribution_model', csvToArray(form.tag_attribution));
    appendTag('campaign', 'campaign_event', csvToArray(form.tag_event));
    appendTag('ad', 'product_category', csvToArray(form.tag_product_category));
    appendTag('ad', 'material_type', csvToArray(form.tag_material_size));

    return applicableTags;
  }, [allTagDefinitions]);

  const buildOperationDescription = useCallback((form: EntryForm) => {
    const channel = platformToChannel(form.platform);
    const dynamicDefs = channel
      ? allTagDefinitions.filter(def => (def.channel || '') === channel)
      : mergeFlatTagDefinitions(allTagDefinitions);
    const accountLabels = getAccountLabels(form.platform, form.object_account_ids);
    const dynamicParts = dynamicDefs
      .map(def => {
        const raw = form.tag_extra?.[def.key];
        if (!raw) return '';
        const labels = csvToArray(raw).map(value => {
          const matched = def.flatOptions?.find(option => option.value === value);
          return matched?.label || value;
        });
        return labels.length ? `${def.label}为${labels.join('、')}` : '';
      })
      .filter(Boolean);

    const linkedParts = [
      accountLabels.length ? `适用广告账户为${accountLabels.join('、')}` : '',
      form.campaign_status ? `系列状态为${form.campaign_status}` : '',
      form.adset_status ? `广告组状态为${form.adset_status}` : '',
      form.ad_status ? `广告状态为${form.ad_status}` : '',
      form.objective_type ? `推广目标为${form.objective_type}` : '',
      form.scope_tag ? `推广目标明细为${form.scope_tag}` : '',
      ...dynamicParts,
    ].filter(Boolean);

    const actionLines = (form.triggerRules || [])
      .map(rule => {
        const actions = (rule.actions || [])
          .filter(item => item.action)
          .map(item => {
            const countryFilterLabel = (item.actionCountryFilter || '')
              .split(',')
              .filter(Boolean)
              .map(value => COUNTRY_FILTER_OPTIONS.find(option => option.value === value)?.label || value)
              .join('、');
            const actionUnit = item.actionValueType === 'pct' ? '%' : '';
            const actionRange = item.actionValue && item.actionValueMax
              ? `${item.actionValue}-${item.actionValueMax}${actionUnit}`
              : item.actionValue
                ? `${item.actionValue}${actionUnit}`
                : '';
            const actionBase = countryFilterLabel ? `${item.action}（作用范围：${countryFilterLabel}）` : item.action;
            return actionRange ? `${actionBase} ${actionRange}` : actionBase;
          });
        return actions.join('；');
      })
      .filter(Boolean);

    const triggerLines = (form.triggerRules || [])
      .filter(rule => rule.conditionGroups?.some(group => group.conditions?.length))
      .map(rule => {
        const groups = rule.conditionGroups || [];
        return groups.map(group => {
          const conditions = (group.conditions || []).map(condition => {
            const windowText = condition.window ? `[${condition.window}]` : '';
            const valueText = (condition.operator === '介于' || condition.operator === '不介于') && condition.valueMax
              ? `${condition.value}~${condition.valueMax}`
              : condition.value;
            const refLabel = condition.metric === '预算受限'
              ? ''
              : condition.valueRef === 'target_pct'
                ? ' 目标值%'
                : condition.valueRef === 'avg_pct'
                  ? ' 均值%'
                  : condition.valueRef === 'market_avg_pct'
                    ? ' 大盘均值%'
                    : condition.valueRef === 'account_avg_pct'
                      ? ' 账户均值%'
                      : '';
            if (condition.metric === '预算受限') {
              return `${windowText}${condition.metric}`;
            }
            return `${windowText}${condition.metric} ${condition.operator} ${valueText}${refLabel}`;
          }).join(group.logic === 'OR' ? ' 或 ' : ' 且 ');
          return groups.length > 1 ? `(${conditions})` : conditions;
        }).join(rule.groupLogic === 'OR' ? ' 或 ' : ' 且 ');
      })
      .filter(Boolean);

    const linkedText = linkedParts.join('，');
    const triggerText = triggerLines.length ? `当 ${triggerLines.join('；')}` : '';
    const actionText = actionLines.join('；');
    const body = [triggerText, actionText ? `则 ${actionText}` : ''].filter(Boolean).join('，');

    if (linkedText && body) return `${linkedText}。${body}`;
    return `${linkedText || body}`.trim();
  }, [allTagDefinitions, getAccountLabels, platformToChannel]);

  const looksAutoGeneratedDescription = useCallback((value: string) => (
    getActionOptions(addForm).some(action => value.startsWith(action))
    || getActionOptions(editForm).some(action => value.startsWith(action))
    || value.startsWith('推广目标为')
  ), [addForm, editForm, getActionOptions]);

  useEffect(() => {
    const generated = buildOperationDescription(addForm);
    setAddForm(prev => {
      if (!generated) {
        if (prev.description === addAutoDescriptionRef.current) {
          addAutoDescriptionRef.current = '';
          return { ...prev, description: '' };
        }
        return prev;
      }
      if (
        !addDescriptionTouchedRef.current
        || !prev.description.trim()
        || prev.description === addAutoDescriptionRef.current
        || looksAutoGeneratedDescription(prev.description)
      ) {
        if (prev.description === generated) {
          addAutoDescriptionRef.current = generated;
          return prev;
        }
        addAutoDescriptionRef.current = generated;
        return { ...prev, description: generated };
      }
      return prev;
    });
  }, [addForm, buildOperationDescription, looksAutoGeneratedDescription]);

  useEffect(() => {
    const generated = buildOperationDescription(editForm);
    setEditForm(prev => {
      if (!generated) {
        if (prev.description === editAutoDescriptionRef.current) {
          editAutoDescriptionRef.current = '';
          return { ...prev, description: '' };
        }
        return prev;
      }
      if (
        (!editDescriptionTouchedRef.current && prev.description === editDescriptionBaselineRef.current)
        || !prev.description.trim()
        || prev.description === editAutoDescriptionRef.current
        || looksAutoGeneratedDescription(prev.description)
      ) {
        if (prev.description === generated) {
          editAutoDescriptionRef.current = generated;
          return prev;
        }
        editAutoDescriptionRef.current = generated;
        return { ...prev, description: generated };
      }
      return prev;
    });
  }, [editForm, buildOperationDescription, looksAutoGeneratedDescription]);

  // ── Add ────────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!canWriteKnowledge) return;
    if (!addForm.title.trim() || !addForm.description.trim()) return;
    setSaving(true);
    try {
      const req: CreateKnowledgeRequest = {
        brand_id: brandId,
        site_id: siteId,
        category: addForm.category,
        title: addForm.title.trim(),
        description: addForm.description.trim(),
        trigger_condition: serializeTriggerRules(addForm.triggerRules),
        diagnostic_tags: addForm.diagnostic_tags,
        metric_conditions: buildMetricConditions(addForm.triggerRules),
        applicable_tags: buildApplicableTags(addForm),
        counter_conditions: addForm.counter_conditions,
        platform: addForm.platform,
        market: addForm.market,
        priority: addForm.priority,
        activation_mode: addForm.activation_mode,
        source: 'manual',
        object_level: addForm.object_level || undefined,
        object_account_ids: addForm.object_account_ids || undefined,
        object_tag: addForm.object_tag || undefined,
        scope_tag: addForm.scope_tag || undefined,
        objective_type: addForm.objective_type || undefined,
        tag_new_old: addForm.tag_new_old || undefined,
        tag_bid_strategy: addForm.tag_bid_strategy || undefined,
        tag_creative_type: addForm.tag_creative_type || undefined,
        tag_funnel: addForm.tag_funnel || undefined,
        tag_series_type: addForm.tag_series_type || undefined,
        tag_attribution: addForm.tag_attribution || undefined,
        tag_event: addForm.tag_event || undefined,
        tag_product_category: addForm.tag_product_category || undefined,
        tag_material_size: addForm.tag_material_size || undefined,
        tag_landing_page: addForm.tag_landing_page || undefined,
        tag_audience_type: addForm.tag_audience_type || undefined,
        tag_extra: serializeTagExtra(addForm.tag_extra) || undefined,
        expected_impact: addForm.expected_impact || undefined,
      };
      await knowledgeApi.create(req);
      setShowAddModal(false);
      setAddForm(emptyForm());
      fetchEntries(1);
      setCurrentPage(1);
      showToast('success', '经验已保存');
    } catch (e) {
      console.error('Failed to create knowledge entry', e);
      showToast('error', '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const buildEditForm = (entry: KnowledgeEntry): EntryForm => ({
    title: entry.title,
    category: entry.category as EntryForm['category'],
    description: entry.description,
    triggerRules: parseTriggerRules(entry.trigger_condition || ''),
    diagnostic_tags: entry.diagnostic_tags || [],
    counter_conditions: entry.counter_conditions || [],
    platform: entry.platform || 'all',
    market: entry.market || 'all',
    priority: (entry.priority as EntryForm['priority']) || 'medium',
    activation_mode: (entry.activation_mode as EntryForm['activation_mode']) || 'immediate',
    object_level: entry.object_level || '',
    object_account_ids: entry.object_account_ids || '',
    object_tag: entry.object_tag || '',
    campaign_status: getApplicableStatus(entry, 'campaign'),
    adset_status: getApplicableStatus(entry, 'adset'),
    ad_status: getApplicableStatus(entry, 'ad'),
    scope_tag: entry.scope_tag || '',
    objective_type: entry.objective_type || '',
    tag_new_old: entry.tag_new_old || '',
    tag_bid_strategy: entry.tag_bid_strategy || '',
    tag_creative_type: entry.tag_creative_type || '',
    tag_funnel: entry.tag_funnel || '',
    tag_series_type: entry.tag_series_type || '',
    tag_attribution: entry.tag_attribution || '',
    tag_event: entry.tag_event || '',
    tag_product_category: entry.tag_product_category || '',
    tag_material_size: entry.tag_material_size || '',
    tag_landing_page: entry.tag_landing_page || '',
    tag_audience_type: entry.tag_audience_type || '',
    tag_extra: parseTagExtra(entry.tag_extra),
    expected_impact: sanitizeExpectedImpact(entry.expected_impact),
  });

  const openEdit = async (entry: KnowledgeEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canWriteKnowledge) return;

    setEditingId(entry.id);
    editDescriptionTouchedRef.current = false;
    editDescriptionBaselineRef.current = entry.description || '';
    editAutoDescriptionRef.current = '';
    setEditForm(buildEditForm(entry));
    setShowEditModal(true);

    try {
      const latest = await knowledgeApi.get(entry.id);
      editDescriptionTouchedRef.current = false;
      editDescriptionBaselineRef.current = latest.description || '';
      editAutoDescriptionRef.current = '';
      setEditForm(buildEditForm(latest));
    } catch (err) {
      console.error('Failed to load knowledge entry detail for edit', err);
    }
  };

  const handleEdit = async () => {
    if (!canWriteKnowledge) return;
    if (!editingId || !editForm.title.trim() || !editForm.description.trim()) return;
    setSaving(true);
    try {
      const req: UpdateKnowledgeRequest = {
        category: editForm.category,
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        trigger_condition: serializeTriggerRules(editForm.triggerRules),
        diagnostic_tags: editForm.diagnostic_tags,
        metric_conditions: buildMetricConditions(editForm.triggerRules),
        applicable_tags: buildApplicableTags(editForm),
        counter_conditions: editForm.counter_conditions,
        platform: editForm.platform,
        market: editForm.market,
        priority: editForm.priority,
        activation_mode: editForm.activation_mode,
        object_level: editForm.object_level || undefined,
        object_account_ids: editForm.object_account_ids || undefined,
        object_tag: editForm.object_tag || undefined,
        scope_tag: editForm.scope_tag || undefined,
        objective_type: editForm.objective_type || undefined,
        tag_new_old: editForm.tag_new_old || undefined,
        tag_bid_strategy: editForm.tag_bid_strategy || undefined,
        tag_creative_type: editForm.tag_creative_type || undefined,
        tag_funnel: editForm.tag_funnel || undefined,
        tag_series_type: editForm.tag_series_type || undefined,
        tag_attribution: editForm.tag_attribution || undefined,
        tag_event: editForm.tag_event || undefined,
        tag_product_category: editForm.tag_product_category || undefined,
        tag_material_size: editForm.tag_material_size || undefined,
        tag_landing_page: editForm.tag_landing_page || undefined,
        tag_audience_type: editForm.tag_audience_type || undefined,
        tag_extra: serializeTagExtra(editForm.tag_extra) || undefined,
        expected_impact: editForm.expected_impact || undefined,
      };
      const updated = await knowledgeApi.update(editingId, req);
      setEntries(prev => prev.map(e => e.id === editingId ? updated : e));
      if (selectedEntry?.id === editingId) setSelectedEntry(updated);
      setShowEditModal(false);
      setEditingId(null);
      showToast('success', '经验已更新');
    } catch (e) {
      console.error('Failed to update knowledge entry', e);
      showToast('error', '更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDeleteKnowledge) return;
    if (!await confirmDialog({ title: '删除经验', message: '确定要删除该条经验吗？', confirmText: '删除', danger: true })) return;
    setDeletingId(id);
    try {
      await knowledgeApi.delete(id);
      if (selectedEntry?.id === id) setSelectedEntry(null);
      fetchEntries(currentPage);
      showToast('success', '经验已删除');
    } catch (err) {
      console.error('Failed to delete knowledge entry', err);
      showToast('error', '删除失败，请重试');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Promote single ─────────────────────────────────────────────────────────
  const handlePromote = async (id: string) => {
    if (!canWriteKnowledge) return;
    try {
      const updated = await knowledgeApi.promote(id);
      setEntries(prev => prev.map(e => e.id === id ? updated : e));
      setCandidateEntries(prev => prev.filter(e => e.id !== id));
      if (selectedEntry?.id === id) setSelectedEntry(updated);
    } catch (e) {
      console.error('Failed to promote entry', e);
    }
  };

  const handleArchive = async (id: string) => {
    if (!canWriteKnowledge) return;
    const confirmed = await confirmDialog({ title: '确认归档', message: '归档后该经验将不再参与AI决策，可以重新编辑恢复。是否继续？', confirmText: '归档', cancelText: '取消' });
    if (!confirmed) return;
    try {
      const updated = await knowledgeApi.archive(id);
      setEntries(prev => prev.map(e => e.id === id ? updated : e));
      if (selectedEntry?.id === id) setSelectedEntry(updated);
    } catch (e) {
      console.error('Failed to archive entry', e);
    }
  };

  // ── Promote all candidates ──────────────────────────────────────────────────
  const confirmAdoptAll = async () => {
    if (!canWriteKnowledge) return;
    for (const entry of candidateEntries) {
      try { await knowledgeApi.promote(entry.id); } catch {}
    }
    setShowConfirmModal(false);
    setShowCandidateModal(false);
    fetchEntries(currentPage);
    fetchCandidates();
  };

  const totalPages = Math.ceil(total / pageSize);

  const renderDynamicTagField = (
    def: FlatTagDefinition,
    form: EntryForm,
    setForm: React.Dispatch<React.SetStateAction<EntryForm>>
  ): React.ReactNode[] => {
    const selectedValues = csvToArray(form.tag_extra[def.key]);
    const showKeywordPrice = def.key === 'keyword_type';
    const keywordPriceKey = `${def.key}_price`;

    const selector = def.treeOptions.some(option => option.children?.length) ? (
      <HierarchicalSelect
        style={{ width: '100%' }}
        options={def.treeOptions}
        value={selectedValues}
        onChange={vals => setForm(prev => ({ ...prev, tag_extra: { ...prev.tag_extra, [def.key]: arrayToCsv(vals) } }))}
        placeholder="请选择"
      />
    ) : (
      <MultiSelect
        style={{ width: '100%' }}
        tagDisplay
        options={def.flatOptions}
        value={selectedValues}
        onChange={vals => setForm(prev => ({ ...prev, tag_extra: { ...prev.tag_extra, [def.key]: arrayToCsv(vals) } }))}
        placeholder="请选择"
      />
    );

    const nodes: React.ReactNode[] = [
      <div key={def.key}>
        <label className="form-label">{def.label}</label>
        {selector}
      </div>,
    ];

    if (showKeywordPrice && selectedValues.length > 0) {
      nodes.push(
        <div key={keywordPriceKey}>
          <label className="form-label">输入值</label>
          <input
            type="text"
            className="form-input"
            style={{ width: '100%', minWidth: 0, height: 40 }}
            placeholder="请输入"
            value={form.tag_extra[keywordPriceKey] || ''}
            onChange={e => {
              const value = e.target.value;
              setForm(prev => ({ ...prev, tag_extra: { ...prev.tag_extra, [keywordPriceKey]: value } }));
            }}
          />
        </div>
      );
    }

    return nodes;
  };

  const renderFixedFieldLabel = (label: string) => {
    const help = FIXED_FIELD_HELP[label];
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span>{label}</span>
        {help ? (
          <span
            title={help}
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--text3)',
              border: '1px solid var(--border2)',
              background: 'transparent',
              cursor: 'help',
              flexShrink: 0,
            }}
          >
            ?
          </span>
        ) : null}
      </span>
    );
  };

  const shouldShowDynamicDefinition = (def: FlatTagDefinition, form: EntryForm) => {
    const newOldDef = tagDefinitions.find(item => item.key === 'new_old_client');
    const newOldValues = newOldDef ? csvToArray(form.tag_extra[newOldDef.key]) : [];
    const hasNewClient = newOldValues.includes('新客');
    const hasOldClient = newOldValues.includes('老客');
    const objectiveDef = tagDefinitions.find(item => item.key === 'objective')
      || tagDefinitions.find(item => item.key === 'purpose');
    const objectiveValues = objectiveDef ? csvToArray(form.tag_extra[objectiveDef.key]) : [];
    const hasRemarketingObjective = objectiveValues.includes('再营销');

    if (def.key === 'new_client_market') return hasNewClient;
    if (def.key === 'old_client_type') return hasOldClient;
    if (def.key === 'customer_type') return hasRemarketingObjective;
    return true;
  };

  const strategyDefinitions = tagDefinitions.filter(def => STRATEGY_KEYS.includes(def.key));
  const creativeDefinitions = tagDefinitions.filter(def => CREATIVE_KEYS.includes(def.key));
  const mergedSupplementDefinitions = tagDefinitions.filter(def =>
    PRODUCT_CHANNEL_KEYS.includes(def.key)
  );
  const otherDefinitions = tagDefinitions.filter(def =>
    !STRATEGY_KEYS.includes(def.key) &&
    !CREATIVE_KEYS.includes(def.key) &&
    !PRODUCT_CHANNEL_KEYS.includes(def.key)
  );

  // ── Render form ─────────────────────────────────────────────────────────────
  const renderForm = (formKind: FormKind, form: EntryForm, setForm: React.Dispatch<React.SetStateAction<EntryForm>>) => (
    <>
      {/* Row 1: 分类 + 优先级 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label className="form-label">经验分类 <span style={{ color: 'var(--danger, #ef4444)' }}>*</span></label>
          <MultiSelect
            single
            style={{ width: '100%' }}
            options={[
              { value: 'bid', label: '出价规则' },
              { value: 'creative', label: '素材经验' },
              { value: 'audience', label: '受众洞察' },
              { value: 'market', label: '市场经验' },
              { value: 'keyword', label: '关键词经验' },
              { value: 'product', label: '商品洞察' },
              { value: 'monitor', label: '数据监控' },
            ]}
            value={form.category ? [form.category] : []}
            onChange={vals => { const v = vals[0] ?? '' as EntryForm['category']; setForm(prev => ({ ...prev, category: v as EntryForm['category'] })); }}
            placeholder="请选择分类"
          />
        </div>
        <div>
          <label className="form-label">优先级</label>
          <MultiSelect
            single
            style={{ width: '100%' }}
            options={[
              { value: 'high', label: '高 — AI 必须遵守' },
              { value: 'medium', label: '中 — AI 优先参考' },
              { value: 'low', label: '低 — AI 仅做参考' },
            ]}
            value={form.priority ? [form.priority] : []}
            onChange={vals => { const v = vals[0] ?? '' as EntryForm['priority']; setForm(prev => ({ ...prev, priority: v as EntryForm['priority'] })); }}
          />
        </div>
      </div>

      {/* 标题 */}
      <div style={{ marginBottom: 16 }}>
        <label className="form-label">经验标题 <span style={{ color: 'var(--danger, #ef4444)' }}>*</span></label>
        <input
          type="text"
          className="form-input"
          style={{ width: '100%' }}
          placeholder="简明描述这条经验，如：CPA 突升自动降价规则"
          value={form.title}
          onChange={e => { const v = e.target.value; setForm(prev => ({ ...prev, title: v })); }}
        />
      </div>

        {/* Section 1: 经验应用场景 */}
      <div style={{ background: 'rgba(99,102,241,.04)', border: '1px solid rgba(99,102,241,.18)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>经验应用场景</span>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>平台 × 账户 × 市场 × 层级 × 明细</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {(() => {
            const hasAccountLevel = csvToArray(form.object_level).includes('账户');
            const selectedLevels = csvToArray(form.object_level);
            const hasCampaignLevel = selectedLevels.includes('广告系列');
            const hasAdsetLevel = selectedLevels.includes('广告组');
            const hasAdLevel = selectedLevels.includes('广告');
            return (
              <>
          <div>
            <label className="form-label">{renderFixedFieldLabel('适用平台')}</label>
            <MultiSelect
              single
              style={{ width: '100%' }}
              options={platformOptions}
              value={form.platform ? [form.platform] : []}
              onChange={vals => { const v = vals[0] ?? ''; setForm(prev => ({ ...prev, platform: v })); }}
            />
          </div>
          <div>
              <label className="form-label">{renderFixedFieldLabel(marketFieldLabel)}</label>
              <MultiSelect
                single
                searchable={station === 'v'}
                style={{ width: '100%' }}
                options={marketOptions}
                value={form.market ? [form.market] : []}
                onChange={vals => {
                  const v = vals[0] ?? '';
                  setForm(prev => ({ ...prev, market: v }));
                }}
              />
          </div>
          <div>
            <label className="form-label">{renderFixedFieldLabel('适用层级')}</label>
            <MultiSelect
              style={{ width: '100%' }}
              tagDisplay
              options={objectLevelOptions}
              value={form.object_level ? form.object_level.split(',').filter(Boolean) : []}
              onChange={vals => setForm(prev => {
                const nextObjectLevel = vals.join(',');
                const hasAccountLevel = vals.includes('账户');
                return {
                  ...prev,
                  object_level: nextObjectLevel,
                  object_account_ids: hasAccountLevel ? prev.object_account_ids : '',
                  campaign_status: vals.includes('广告系列') ? prev.campaign_status : '',
                  adset_status: vals.includes('广告组') ? prev.adset_status : '',
                  ad_status: vals.includes('广告') ? prev.ad_status : '',
                };
              })}
              placeholder="请选择"
            />
          </div>
          {hasCampaignLevel && (
            <div>
              <label className="form-label">{renderFixedFieldLabel('系列状态')}</label>
              <MultiSelect
                single
                style={{ width: '100%' }}
                options={OBJECT_STATUS_OPTIONS}
                value={form.campaign_status ? [form.campaign_status] : []}
                onChange={vals => {
                  const v = vals[0] ?? '';
                  setForm(prev => ({ ...prev, campaign_status: v }));
                }}
                placeholder="请选择"
              />
            </div>
          )}
          {hasAdsetLevel && (
            <div>
              <label className="form-label">{renderFixedFieldLabel('广告组状态')}</label>
              <MultiSelect
                single
                style={{ width: '100%' }}
                options={OBJECT_STATUS_OPTIONS}
                value={form.adset_status ? [form.adset_status] : []}
                onChange={vals => {
                  const v = vals[0] ?? '';
                  setForm(prev => ({ ...prev, adset_status: v }));
                }}
                placeholder="请选择"
              />
            </div>
          )}
          {hasAdLevel && (
            <div>
              <label className="form-label">{renderFixedFieldLabel('广告状态')}</label>
              <MultiSelect
                single
                style={{ width: '100%' }}
                options={OBJECT_STATUS_OPTIONS}
                value={form.ad_status ? [form.ad_status] : []}
                onChange={vals => {
                  const v = vals[0] ?? '';
                  setForm(prev => ({ ...prev, ad_status: v }));
                }}
                placeholder="请选择"
              />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <label className="form-label">{renderFixedFieldLabel(hasAccountLevel ? '适用广告账户' : '适用明细')}</label>
            {hasAccountLevel ? (
              <MultiSelect
                style={{ width: '100%', minWidth: 0 }}
                tagDisplay
                searchable
                options={getAccountOptions(form.platform)}
                value={csvToArray(form.object_account_ids)}
                onChange={vals => setForm(prev => ({ ...prev, object_account_ids: arrayToCsv(vals) }))}
                placeholder={accountsLoading ? '加载广告账户中...' : '请选择'}
              />
            ) : (
              <MultiSelect
                style={{ width: '100%' }}
                tagDisplay
                options={FIELD_OPTIONS.object_tag}
                value={form.object_tag ? form.object_tag.split(',').filter(Boolean) : []}
                onChange={vals => setForm(prev => ({ ...prev, object_tag: vals.join(',') }))}
                placeholder="请选择"
              />
            )}
          </div>
          {hasAccountLevel && (
            <div style={{ minWidth: 0 }}>
              <label className="form-label">{renderFixedFieldLabel('适用明细')}</label>
              <MultiSelect
                style={{ width: '100%', minWidth: 0 }}
                tagDisplay
                options={FIELD_OPTIONS.object_tag}
                value={form.object_tag ? form.object_tag.split(',').filter(Boolean) : []}
                onChange={vals => setForm(prev => ({ ...prev, object_tag: vals.join(',') }))}
                placeholder="请选择"
              />
            </div>
          )}
              </>
            );
          })()}
        </div>
      </div>

      {strategyDefinitions.length > 0 && (
        <div style={{ background: 'rgba(245,158,11,.04)', border: '1px solid rgba(245,158,11,.16)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>投放策略</span>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>目标 × 受众 × 系列 × 竞价 × 归因</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {strategyDefinitions
              .filter(def => shouldShowDynamicDefinition(def, form))
              .flatMap(def => renderDynamicTagField(def, form, setForm))}
          </div>
        </div>
      )}

      {creativeDefinitions.length > 0 && (
        <div style={{ background: 'rgba(236,72,153,.04)', border: '1px solid rgba(236,72,153,.16)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>素材策略</span>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>素材 × 商品 × 落地页 × 版位</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {creativeDefinitions.flatMap(def => renderDynamicTagField(def, form, setForm))}
          </div>
        </div>
      )}

      {mergedSupplementDefinitions.length > 0 && (
        <div style={{ background: 'rgba(14,165,233,.04)', border: '1px solid rgba(14,165,233,.16)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>商品与渠道策略</span>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>活动 × 定价 × 分组 × 款式 × 渠道 × 设备</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[...mergedSupplementDefinitions, ...otherDefinitions].flatMap(def => renderDynamicTagField(def, form, setForm))}
          </div>
        </div>
      )}

      <div style={{ background: 'rgba(16,185,129,.05)', border: '1px solid rgba(16,185,129,.18)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>诊断标签</span>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>用于表达经验适用的异常类型</span>
        </div>
        <MultiSelect
          style={{ width: '100%' }}
          tagDisplay
          options={DIAGNOSTIC_TAG_OPTIONS}
          value={form.diagnostic_tags}
          onChange={vals => setForm(prev => ({ ...prev, diagnostic_tags: vals }))}
          placeholder="请选择诊断标签"
        />
      </div>

      {/* 触发条件（结构化） */}
      <div style={{ marginBottom: 16 }}>
        <label className="form-label">触发条件（结构化，可选）</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 6 }}>
          {form.triggerRules.map((rule, rIdx) => (
            <div key={rIdx} style={{ background: 'var(--surface2, rgba(0,0,0,.02))', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 14 }}>
              {/* 头部：移除 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>规则 {rIdx + 1}</span>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 11, color: 'var(--danger, #ef4444)', padding: '2px 8px' }}
                  onClick={() => setForm(prev => ({ ...prev, triggerRules: prev.triggerRules.filter((_, i) => i !== rIdx) }))}
                >移除规则</button>
              </div>

              {/* 条件组列表 */}
              {rule.conditionGroups.map((group, gIdx) => (
                <React.Fragment key={gIdx}>
                  {/* 组间连接符 */}
                  {gIdx > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
                      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                      <MultiSelect
                        single
                        clearable={false}
                        style={{ width: 64 }}
                        options={[{ value: 'AND', label: '且' }, { value: 'OR', label: '或' }]}
                        value={[rule.groupLogic || 'AND']}
                        onChange={vals => { const v = (vals[0] ?? 'AND') as 'AND' | 'OR'; setForm(prev => { const rs = [...prev.triggerRules]; rs[rIdx] = { ...rs[rIdx], groupLogic: v }; return { ...prev, triggerRules: rs }; }); }}
                      />
                      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    </div>
                  )}

                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: 10, marginBottom: 4 }}>
                    {/* 组头：组内逻辑 + 移除组 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>满足</span>
                      <MultiSelect
                        single
                        clearable={false}
                        style={{ width: 72 }}
                        options={[{ value: 'AND', label: '全部' }, { value: 'OR', label: '任一' }]}
                        value={[group.logic || 'AND']}
                        onChange={vals => { const v = (vals[0] ?? 'AND') as 'AND' | 'OR'; setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; gs[gIdx] = { ...gs[gIdx], logic: v }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; }); }}
                      />
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>以下条件时触发</span>
                      {rule.conditionGroups.length > 1 && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--danger, #ef4444)', padding: '2px 6px' }}
                          onClick={() => setForm(prev => { const rs = [...prev.triggerRules]; rs[rIdx] = { ...rs[rIdx], conditionGroups: rs[rIdx].conditionGroups.filter((_, i) => i !== gIdx) }; return { ...prev, triggerRules: rs }; })}
                        >移除条件组</button>
                      )}
                    </div>

                    {/* 条件行 */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                      {group.conditions.map((cond, cIdx) => (
                        <div key={cIdx} style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <MultiSelect
                            single
                            clearable={false}
                            style={{ width: 90, flexShrink: 0 }}
                            options={buildWindowOptions(cond.window)}
                            value={[cond.window || '不限']}
                            onChange={vals => {
                              const v = vals[0] ?? '';
                              if (v === '__custom__') {
                                openCustomWindowModal(formKind, rIdx, gIdx, cIdx, cond.window);
                                return;
                              }
                              setForm(prev => {
                                const rs = [...prev.triggerRules];
                                const gs = [...rs[rIdx].conditionGroups];
                                const cs = [...gs[gIdx].conditions];
                                cs[cIdx] = { ...cs[cIdx], window: v === '不限' ? undefined : v };
                                gs[gIdx] = { ...gs[gIdx], conditions: cs };
                                rs[rIdx] = { ...rs[rIdx], conditionGroups: gs };
                                return { ...prev, triggerRules: rs };
                              });
                            }}
                          />
                          <MultiSelect
                            single
                            searchable
                            clearable={false}
                            style={{ width: 90, flexShrink: 0 }}
                            options={triggerMetricOptions}
                            value={cond.metric ? [cond.metric] : []}
                            onChange={vals => { const v = vals[0] ?? ''; setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; const cs = [...gs[gIdx].conditions]; cs[cIdx] = v === '预算受限' ? { ...cs[cIdx], metric: v, operator: '等于', value: '1', valueMax: '', valueRef: 'abs' } : { ...cs[cIdx], metric: v }; gs[gIdx] = { ...gs[gIdx], conditions: cs }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; }); }}
                          />
                          {cond.metric !== '预算受限' && (
                            <>
                              <MultiSelect
                                single
                                clearable={false}
                                style={{ width: 85, flexShrink: 0 }}
                                options={[
                                  { value: '大于', label: '>' },
                                  { value: '大于等于', label: '≥' },
                                  { value: '等于', label: '=' },
                                  { value: '小于等于', label: '≤' },
                                  { value: '小于', label: '<' },
                                  { value: '介于', label: '介于' },
                                  { value: '不介于', label: '不介于' },
                                ]}
                                value={cond.operator ? [cond.operator] : []}
                                onChange={vals => { const v = vals[0] ?? ''; setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; const cs = [...gs[gIdx].conditions]; cs[cIdx] = { ...cs[cIdx], operator: v, valueMax: v === '介于' || v === '不介于' ? (cs[cIdx].valueMax || '') : '' }; gs[gIdx] = { ...gs[gIdx], conditions: cs }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; }); }}
                              />
                              <input
                                style={{ width: 64, flexShrink: 0, padding: '7px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 500, outline: 'none' }}
                                placeholder="数值"
                                type="text"
                                value={cond.value}
                                onChange={e => { const v = e.target.value; setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; const cs = [...gs[gIdx].conditions]; cs[cIdx] = { ...cs[cIdx], value: v }; gs[gIdx] = { ...gs[gIdx], conditions: cs }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; }); }}
                              />
                              {(cond.operator === '介于' || cond.operator === '不介于') && (
                                <input
                                  style={{ width: 64, flexShrink: 0, padding: '7px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 500, outline: 'none' }}
                                  placeholder="结束值"
                                  type="text"
                                  value={cond.valueMax || ''}
                                  onChange={e => { const v = e.target.value; setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; const cs = [...gs[gIdx].conditions]; cs[cIdx] = { ...cs[cIdx], valueMax: v }; gs[gIdx] = { ...gs[gIdx], conditions: cs }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; }); }}
                                />
                              )}
                              <MultiSelect
                                single
                                clearable={false}
                                style={{ width: 120, flexShrink: 0 }}
                                options={[
                                  { value: 'abs', label: '绝对值' },
                                  { value: 'target_pct', label: '目标值%' },
                                  { value: 'avg_pct', label: '均值%' },
                                  { value: 'market_avg_pct', label: '大盘均值%' },
                                  { value: 'account_avg_pct', label: '账户均值%' },
                                ]}
                                value={[cond.valueRef || 'abs']}
                                onChange={vals => { const v = (vals[0] ?? 'abs') as TriggerCondition['valueRef']; setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; const cs = [...gs[gIdx].conditions]; cs[cIdx] = { ...cs[cIdx], valueRef: v }; gs[gIdx] = { ...gs[gIdx], conditions: cs }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; }); }}
                              />
                            </>
                          )}
                          {group.conditions.length > 1 && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ fontSize: 12, color: 'var(--danger, #ef4444)', padding: '2px 6px' }}
                              onClick={() => setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; gs[gIdx] = { ...gs[gIdx], conditions: gs[gIdx].conditions.filter((_, i) => i !== cIdx) }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; })}
                            >×</button>
                          )}
                        </div>
                      ))}
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ alignSelf: 'flex-start', fontSize: 11 }}
                        onClick={() => setForm(prev => { const rs = [...prev.triggerRules]; const gs = [...rs[rIdx].conditionGroups]; gs[gIdx] = { ...gs[gIdx], conditions: [...gs[gIdx].conditions, emptyCondition()] }; rs[rIdx] = { ...rs[rIdx], conditionGroups: gs }; return { ...prev, triggerRules: rs }; })}
                      >+ 添加条件</button>
                    </div>
                  </div>
                </React.Fragment>
              ))}

              {/* 添加条件组 */}
              <button
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 11, marginTop: 6, marginBottom: 10, color: 'var(--text3)' }}
                onClick={() => setForm(prev => { const rs = [...prev.triggerRules]; rs[rIdx] = { ...rs[rIdx], conditionGroups: [...rs[rIdx].conditionGroups, emptyGroup()] }; return { ...prev, triggerRules: rs }; })}
              >+ 添加条件组（支持括号内 AND/OR）</button>

              {/* 执行动作 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                {(rule.actions || []).map((actionItem, aIdx) => (
                  <div key={aIdx} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--text3)', width: 16 }}>{aIdx === 0 ? '则' : '并'}</span>
                    <MultiSelect
                      single
                      clearable={false}
                      style={{ width: 120 }}
                      options={getActionOptions(form).map(a => ({ value: a, label: a }))}
                      value={actionItem.action ? [actionItem.action] : []}
                      placeholder="不执行操作"
                      onChange={vals => {
                        const v = vals[0] ?? '';
                        setForm(prev => {
                          const rs = [...prev.triggerRules];
                          const actions = [...(rs[rIdx].actions || [emptyAction()])];
                          actions[aIdx] = {
                            ...actions[aIdx],
                            action: v,
                            actionValue: BUDGET_RANGE_ACTIONS.has(v) || !NO_VALUE_ACTIONS.has(v) ? actions[aIdx].actionValue : '',
                            actionValueMax: BUDGET_RANGE_ACTIONS.has(v) ? actions[aIdx].actionValueMax : '',
                          };
                          rs[rIdx] = { ...rs[rIdx], actions };
                          return { ...prev, triggerRules: rs };
                        });
                      }}
                    />
                    {station === 'v' && actionItem.action && (
                      <MultiSelect
                        clearable={false}
                        style={{ width: 120 }}
                        options={COUNTRY_FILTER_OPTIONS}
                        value={actionItem.actionCountryFilter ? actionItem.actionCountryFilter.split(',').filter(Boolean) : []}
                        placeholder="作用范围"
                        onChange={vals => {
                          setForm(prev => {
                            const rs = [...prev.triggerRules];
                            const actions = [...(rs[rIdx].actions || [emptyAction()])];
                            actions[aIdx] = { ...actions[aIdx], actionCountryFilter: vals.join(',') };
                            rs[rIdx] = { ...rs[rIdx], actions };
                            return { ...prev, triggerRules: rs };
                          });
                        }}
                      />
                    )}
                    {actionItem.action && !NO_VALUE_ACTIONS.has(actionItem.action) && (
                      <>
                        <input
                          style={{ width: 90, padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 500, outline: 'none' }}
                          placeholder="20"
                          value={actionItem.actionValue}
                          onChange={e => {
                            const v = e.target.value;
                            setForm(prev => {
                              const rs = [...prev.triggerRules];
                              const actions = [...(rs[rIdx].actions || [emptyAction()])];
                              actions[aIdx] = { ...actions[aIdx], actionValue: v };
                              rs[rIdx] = { ...rs[rIdx], actions };
                              return { ...prev, triggerRules: rs };
                            });
                          }}
                        />
                        {BUDGET_RANGE_ACTIONS.has(actionItem.action) && (
                          <>
                            <span style={{ fontSize: 12, color: 'var(--text3)' }}>-</span>
                            <input
                              style={{ width: 90, padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 500, outline: 'none' }}
                              placeholder="30"
                              value={actionItem.actionValueMax || ''}
                              onChange={e => {
                                const v = e.target.value;
                                setForm(prev => {
                                  const rs = [...prev.triggerRules];
                                  const actions = [...(rs[rIdx].actions || [emptyAction()])];
                                  actions[aIdx] = { ...actions[aIdx], actionValueMax: v };
                                  rs[rIdx] = { ...rs[rIdx], actions };
                                  return { ...prev, triggerRules: rs };
                                });
                              }}
                            />
                          </>
                        )}
                        <MultiSelect
                          single
                          clearable={false}
                          style={{ width: 80 }}
                          options={[{ value: 'pct', label: '%' }, { value: 'abs', label: '绝对值' }]}
                          value={[actionItem.actionValueType || 'pct']}
                          onChange={vals => {
                            const v = (vals[0] ?? 'pct') as 'pct' | 'abs';
                            setForm(prev => {
                              const rs = [...prev.triggerRules];
                              const actions = [...(rs[rIdx].actions || [emptyAction()])];
                              actions[aIdx] = { ...actions[aIdx], actionValueType: v };
                              rs[rIdx] = { ...rs[rIdx], actions };
                              return { ...prev, triggerRules: rs };
                            });
                          }}
                        />
                      </>
                    )}
                    {(rule.actions || []).length > 1 && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 12, color: 'var(--danger, #ef4444)', padding: '2px 6px' }}
                        onClick={() => setForm(prev => {
                          const rs = [...prev.triggerRules];
                          rs[rIdx] = { ...rs[rIdx], actions: (rs[rIdx].actions || []).filter((_, i) => i !== aIdx) };
                          return { ...prev, triggerRules: rs };
                        })}
                      >×</button>
                    )}
                  </div>
                ))}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ alignSelf: 'flex-start', fontSize: 11 }}
                  onClick={() => setForm(prev => {
                    const rs = [...prev.triggerRules];
                    rs[rIdx] = { ...rs[rIdx], actions: [...(rs[rIdx].actions || []), emptyAction()] };
                    return { ...prev, triggerRules: rs };
                  })}
                >+ 添加结果</button>
              </div>
            </div>
          ))}
          {form.triggerRules.length === 0 && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: 'flex-start', color: 'var(--text3)' }}
              onClick={() => setForm(prev => ({ ...prev, triggerRules: [emptyRule()] }))}
            >+ 添加触发条件</button>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>结构化条件将直接转化为 AI 规则引擎规则，优先级高于文本描述</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label className="form-label">反例条件<span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>命中后经验将降权或不采用</span></label>
        <MultiSelect
          style={{ width: '100%' }}
          tagDisplay
          options={COUNTER_CONDITION_OPTIONS}
          value={form.counter_conditions}
          onChange={vals => setForm(prev => ({ ...prev, counter_conditions: vals }))}
          placeholder="请选择反例条件"
        />
      </div>

      {/* 具体操作 */}
      <div style={{ marginBottom: 16 }}>
        <label className="form-label">具体操作（Do）<span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>执行什么动作</span></label>
        <textarea
          className="form-input"
          style={{ width: '100%', minHeight: 92, resize: 'vertical' }}
          placeholder="例如：降低出价 15%，同时暂停 CPA 最高的 2 条广告，将预算转移至 ROAS > 3 的广告组"
          value={form.description}
          onChange={e => {
            const v = e.target.value;
            if (formKind === 'edit') {
              editDescriptionTouchedRef.current = true;
            } else {
              addDescriptionTouchedRef.current = true;
            }
            setForm(prev => ({ ...prev, description: v }));
          }}
        />
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>会自动根据上方结构化规则生成自然语言，你也可以继续补充或调整。</div>
      </div>

      {/* 预期效果 */}
      <div style={{ marginBottom: 16 }}>
        <label className="form-label">预期效果（Expect）<span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>可选 · 预期指标变化</span></label>
        <textarea
          className="form-input"
          style={{ width: '100%', minHeight: 72, resize: 'vertical' }}
          placeholder="例如：CPA 预计下降 20-30%，ROAS 提升至 3.5+，24h 内见效"
          value={form.expected_impact}
          onChange={e => { const v = e.target.value; setForm(prev => ({ ...prev, expected_impact: v })); }}
        />
      </div>

      {/* 生效方式 */}
      <div style={{ marginBottom: 4 }}>
        <label className="form-label">生效方式</label>
        <div className="option-cards">
          {([
            { value: 'immediate', icon: '⚡', title: '立即生效', desc: '保存后 AI 立即应用此经验' },
            { value: 'gradual',   icon: '🧪', title: '灰度验证', desc: '先在 10% 流量上试运行' },
            { value: 'record_only', icon: '📋', title: '仅记录', desc: '不执行，仅在建议中展示' },
          ] as const).map(opt => (
            <div
              key={opt.value}
              className={`option-card ${form.activation_mode === opt.value ? 'selected' : ''}`}
              onClick={() => { const v = opt.value; setForm(prev => ({ ...prev, activation_mode: v })); }}
            >
              <div className="oc-icon">{opt.icon}</div>
              <div className="oc-title">{opt.title}</div>
              <div className="oc-desc">{opt.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="page-section" style={{ display: 'block' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 18px', borderRadius: 'var(--radius)', color: '#fff', fontSize: 13,
          background: toast.type === 'success' ? 'var(--green)' : '#ef4444',
          boxShadow: '0 4px 16px rgba(0,0,0,.3)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {toast.type === 'success' ? '✓' : '✕'} {toast.message}
        </div>
      )}

      {/* Stats Overview */}
      <div className="knowledge-stats">
        <div className="knowledge-stat-card">
          <div className="ks-value" style={{ color: 'var(--primary-light)' }}>{total}</div>
          <div className="ks-label">总经验条目</div>
          <div className="ks-change" style={{ color: 'var(--text3)' }}>当前筛选结果</div>
        </div>
        <div className="knowledge-stat-card">
          <div className="ks-value" style={{ color: 'var(--green)' }}>{candidateEntries.length}</div>
          <div className="ks-label">AI 学习候选</div>
          <div className="ks-change" style={{ color: candidateEntries.length > 0 ? 'var(--amber)' : 'var(--text3)' }}>
            {candidateEntries.length > 0 ? '待确认采纳' : '暂无待确认'}
          </div>
        </div>
        <div className="knowledge-stat-card">
          <div className="ks-value" style={{ color: 'var(--amber)' }}>
            {entries.filter(i => i.status === 'active').length}
          </div>
          <div className="ks-label">当前页已生效</div>
          <div className="ks-change" style={{ color: 'var(--text3)' }}>AI 决策时自动引用</div>
        </div>
        <div className="knowledge-stat-card">
          <div className="ks-value" style={{ color: 'var(--cyan)' }}>
            {canViewFullKnowledge && entries.length > 0
              ? Math.round(entries.reduce((a, i) => a + i.usage_count, 0) / entries.length)
              : canViewFullKnowledge ? 0 : '***'}
          </div>
          <div className="ks-label">平均引用次数</div>
          <div className="ks-change" style={{ color: 'var(--text3)' }}>{canViewFullKnowledge ? '当前页统计' : '需 Leader 权限查看完整数据'}</div>
        </div>
      </div>

      {/* AI Candidate Alert */}
      {candidateEntries.length > 0 && (
        <div className="ai-learning-card mb-24">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--primary-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🧠</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>AI 自动学习发现 {candidateEntries.length} 条新经验</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{isMobile ? 'AI 识别出重复决策模式，建议提升为正式规则' : '基于近 30 天审批记录分析，AI 识别出重复的决策模式，建议提升为正式规则'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowCandidateModal(true)}>查看候选</button>
              {canWriteKnowledge && (
                <button className="btn btn-primary btn-sm" onClick={() => setShowConfirmModal(true)}>全部采纳</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {isMobile ? (
        <div className="toolbar-mobile" style={{ marginBottom: 20 }}>
          <div className="toolbar-mobile-row tabs-row">
            <div className="page-tabs">
              {(['all', 'bid', 'creative', 'audience', 'market', 'keyword', 'product', 'monitor'] as const).map(cat => (
                <div
                  key={cat}
                  className={`page-tab ${activeCat === cat ? 'active' : ''}`}
                  onClick={() => handleCatChange(cat)}
                >
                  {cat === 'all' ? '全部' : catMeta[cat].label}
                </div>
              ))}
            </div>
          </div>
          <div className="toolbar-mobile-row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0 }}>
              <MultiSelect single options={[{value:'all',label:'全部来源'},{value:'manual',label:'人工录入'},{value:'ai_learned',label:'AI 学习'}]} value={[sourceFilter]} onChange={(vals) => handleSourceChange((vals[0] ?? 'all') as typeof sourceFilter)} style={{ flex: 1, minWidth: 0 }} />
              <MultiSelect single options={platformOptions} value={[platformFilter]} onChange={(vals) => handlePlatformChange(vals[0] ?? 'all')} style={{ flex: 1, minWidth: 0 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0 }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <input
                  type="text"
                  className="filter-select"
                  style={{ paddingLeft: 28, width: '100%' }}
                  placeholder="搜索..."
                  value={searchInput}
                  onChange={e => handleSearchChange(e.target.value)}
                />
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {canViewFullKnowledge && (
                <button className="btn btn-outline btn-sm" onClick={() => setShowExportModal(true)}>导出</button>
              )}
                {canWriteKnowledge && (
                  <button className="btn btn-primary btn-sm" onClick={() => { addDescriptionTouchedRef.current = false; addAutoDescriptionRef.current = ''; setAddForm(emptyForm()); setShowAddModal(true); }}>+ 添加</button>
                )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div className="page-tabs">
              {(['all', 'bid', 'creative', 'audience', 'market', 'keyword', 'product', 'monitor'] as const).map(cat => (
                <div
                  key={cat}
                  className={`page-tab ${activeCat === cat ? 'active' : ''}`}
                  onClick={() => handleCatChange(cat)}
                >
                  {cat === 'all' ? '全部' : catMeta[cat].label}
                </div>
              ))}
            </div>
            <div className="divider-v" />
            <MultiSelect single options={[{value:'all',label:'全部来源'},{value:'manual',label:'人工录入'},{value:'ai_learned',label:'AI 学习'}]} value={[sourceFilter]} onChange={(vals) => handleSourceChange((vals[0] ?? 'all') as typeof sourceFilter)} />
            <MultiSelect single options={platformOptions} value={[platformFilter]} onChange={(vals) => handlePlatformChange(vals[0] ?? 'all')} />
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="filter-select"
                style={{ paddingLeft: 28, width: 180 }}
                placeholder="搜索标题 / 描述..."
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
              />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canViewFullKnowledge && (
              <button className="btn btn-outline btn-sm" onClick={() => setShowExportModal(true)}>导出经验</button>
            )}
              {canWriteKnowledge && (
                <button className="btn btn-primary" onClick={() => { addDescriptionTouchedRef.current = false; addAutoDescriptionRef.current = ''; setAddForm(emptyForm()); setShowAddModal(true); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}>
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                添加经验
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cards Grid */}
      {loading ? (
        <div className="knowledge-grid">
          <div className="card" style={{ gridColumn: '1 / -1', padding: 0 }}>
            <TableLoadingState />
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>暂无数据</div>
      ) : (
        <div className="knowledge-grid">
          {entries.map(exp => {
            const cm = catMeta[exp.category] || catMeta.bid;
            const pLabel = platformIcons[exp.platform] || exp.platform;
            return (
              <div
                className="card knowledge-card"
                key={exp.id}
                style={{ cursor: 'pointer', transition: 'all .2s' }}
                onClick={() => setSelectedEntry(exp)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 10, fontWeight: 700, color: cm.color, background: cm.bg }}>{cm.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {exp.source === 'ai_learned'
                      ? <span style={{ fontSize: 10, color: 'var(--primary-light)', background: 'var(--primary-bg)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>🤖 AI 学习</span>
                      : <span style={{ fontSize: 10, color: 'var(--text3)', background: 'rgba(0,0,0,.03)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>✍ 人工录入</span>
                    }
                    {exp.status === 'active'
                      ? <span style={{ color: 'var(--green)', fontSize: 10, fontWeight: 600 }}>● 已生效</span>
                      : <span style={{ color: 'var(--amber)', fontSize: 10, fontWeight: 600 }}>◐ 候选</span>
                    }
                  </div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, lineHeight: 1.4 }}>{displayText(exp.title)}</div>
                {!canViewFullKnowledge && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>机密数据，需 Leader 权限查看完整内容</div>
                )}
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 14, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {displayText(exp.description)}
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                  <span
                    className={`ch-tag ${exp.platform === 'tiktok' ? 'tt' : exp.platform === 'facebook' ? 'fb' : exp.platform === 'google' ? 'gg' : ''}`}
                    style={exp.platform === 'all' ? { background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' } : {}}
                  >
                    {pLabel}
                  </span>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: 'rgba(0,0,0,.03)', color: 'var(--text3)', border: '1px solid var(--border)' }}>
                    {getMarketLabel(exp.market)}
                  </span>
                </div>
                {exp.action && (
                  <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 12, fontStyle: 'italic' }}>
                    ✓ {displayText(exp.action)}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                    {exp.created_at ? exp.created_at.split('T')[0] : ''}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 11, height: 11, flexShrink: 0 }}>
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                      </svg>
                      {displayUsageCount(exp.usage_count)}次引用
                    </span>
                    {canWriteKnowledge && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ padding: '2px 8px', fontSize: 11 }}
                        onClick={e => openEdit(exp, e)}
                      >
                        编辑
                      </button>
                    )}
                    {canDeleteKnowledge && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ padding: '2px 8px', fontSize: 11, color: 'var(--danger, #ef4444)', minWidth: 44 }}
                        disabled={deletingId === exp.id}
                        onClick={e => handleDelete(exp.id, e)}
                      >
                        {deletingId === exp.id ? <span className="spinner" style={{ width: 11, height: 11, borderWidth: 2 }} /> : '删除'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onChange={setCurrentPage}
          total={`共 ${total} 条经验`}
          pageSize={pageSize}
          pageSizeOptions={[9, 18, 36]}
          onPageSizeChange={nextPageSize => { setPageSize(nextPageSize); setCurrentPage(1); }}
          disabled={loading}
          style={{ marginTop: 20 }}
        />
      )}

      {/* ── Add Modal ─────────────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="modal-overlay open">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3>📝 存入经验</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <div className="modal-body">{renderForm('add', addForm, setAddForm)}</div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAddModal(false)} disabled={saving}>取消</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving} style={{ minWidth: 80 }}>
                {saving ? <><span className="spinner" style={{ width: 13, height: 13, marginRight: 6, borderWidth: 2 }} />保存中</> : '保存经验'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ────────────────────────────────────────────────────── */}
      {showEditModal && (
        <div className="modal-overlay open">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3>编辑经验</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="modal-body">{renderForm('edit', editForm, setEditForm)}</div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowEditModal(false)} disabled={saving}>取消</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving} style={{ minWidth: 88 }}>
                {saving ? <><span className="spinner" style={{ width: 13, height: 13, marginRight: 6, borderWidth: 2 }} />保存中</> : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Candidate Modal ───────────────────────────────────────────────── */}
      {showCandidateModal && (
        <div className="modal-overlay open">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3>AI 学习候选经验</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCandidateModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {candidateEntries.map(item => {
                  const cm = catMeta[item.category] || catMeta.bid;
                  return (
                    <div key={item.id} className="card" style={{ margin: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 700, color: cm.color, background: cm.bg }}>{cm.label}</span>
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>{item.created_at?.split('T')[0]}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{displayText(item.title)}</div>
                      {!canViewFullKnowledge && (
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6 }}>机密数据，需 Leader 权限查看完整内容</div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 8 }}>{displayText(item.description)}</div>
                      {item.action && (
                        <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 8 }}>✓ {displayText(item.action)}</div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>引用: {displayUsageCount(item.usage_count)}次</span>
                        {canWriteKnowledge && (
                          <button className="btn btn-primary btn-sm" onClick={() => handlePromote(item.id)}>采纳</button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {candidateEntries.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>暂无候选经验</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCandidateModal(false)}>关闭</button>
              {canWriteKnowledge && candidateEntries.length > 0 && (
                <button className="btn btn-primary" onClick={() => setShowConfirmModal(true)}>全部采纳</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm All Modal ─────────────────────────────────────────────── */}
      {showConfirmModal && (
        <div className="modal-overlay open">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>确认采纳</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowConfirmModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text2)' }}>
                确定要采纳全部 {candidateEntries.length} 条 AI 学习经验吗？这些经验将被提升为正式规则并在 AI 决策时自动引用。
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowConfirmModal(false)}>取消</button>
              <button className="btn btn-primary" onClick={confirmAdoptAll}>确认采纳</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Export Modal ──────────────────────────────────────────────────── */}
      {showExportModal && canViewFullKnowledge && (
        <div className="modal-overlay open">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>导出经验库</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowExportModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>导出格式</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['pdf', 'excel', 'csv'] as const).map(fmt => (
                    <button
                      key={fmt}
                      className={`btn btn-sm ${exportFormat === fmt ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => setExportFormat(fmt)}
                    >
                      {fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(18,16,184,.1)' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>将导出 {total} 条经验数据（当前筛选条件下）</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowExportModal(false)}>取消</button>
              <button className="btn btn-primary" onClick={() => { setShowExportModal(false); showAlert(`经验库已导出为 ${exportFormat.toUpperCase()} 格式`, { type: 'success' }); }}>确认导出</button>
            </div>
          </div>
        </div>
      )}

      {customWindowTarget && (
        <div className="modal-overlay open">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>自定义近多少天</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => { setCustomWindowTarget(null); setCustomWindowDays(''); }}>✕</button>
            </div>
            <div className="modal-body">
              <label className="form-label">天数</label>
              <input
                type="number"
                min="1"
                step="1"
                className="form-input"
                style={{ width: '100%' }}
                placeholder="请输入天数，例如 21"
                value={customWindowDays}
                onChange={e => setCustomWindowDays(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyCustomWindow();
                  }
                }}
              />
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>确认后将保存为“近N天”，例如“近21天”。</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setCustomWindowTarget(null); setCustomWindowDays(''); }}>取消</button>
              <button className="btn btn-primary" onClick={applyCustomWindow}>确认</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Detail Modal ──────────────────────────────────────────────────── */}
      {selectedEntry && (
        <div className="modal-overlay open">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 10, fontWeight: 700, color: catMeta[selectedEntry.category]?.color, background: catMeta[selectedEntry.category]?.bg }}>
                  {catMeta[selectedEntry.category]?.label}
                </span>
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                  color: selectedEntry.status === 'active' ? 'var(--green)' : selectedEntry.status === 'candidate' ? 'var(--amber)' : 'var(--text3)',
                  background: selectedEntry.status === 'active' ? 'rgba(34,197,94,.1)' : selectedEntry.status === 'candidate' ? 'rgba(245,158,11,.1)' : 'rgba(0,0,0,.04)',
                  border: '1px solid var(--border)',
                }}>
                  {selectedEntry.status === 'active' ? '已采纳' : selectedEntry.status === 'candidate' ? '候选中' : '已归档'}
                </span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedEntry(null)}>✕</button>
            </div>
            {/* Body */}
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
            {!canViewFullKnowledge && (
              <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'rgba(0,0,0,.02)', fontSize: 12, color: 'var(--text3)' }}>
                当前为脱敏视图，完整经验内容需 Leader 权限查看。
              </div>
            )}
            <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, lineHeight: 1.4 }}>{displayText(selectedEntry.title)}</h4>
            {/* Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div style={{ padding: '14px 16px', background: 'var(--primary-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(18,16,184,.1)' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>AI引用次数</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary-light)' }}>{displayUsageCount(selectedEntry.usage_count)}</div>
              </div>
              <div style={{ padding: '14px 16px', background: 'rgba(34,197,94,.05)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(34,197,94,.15)' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>应用成功率</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>—</div>
              </div>
            </div>
            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>平台</div>
                <span
                  className={`ch-tag ${selectedEntry.platform === 'tiktok' ? 'tt' : selectedEntry.platform === 'facebook' ? 'fb' : selectedEntry.platform === 'google' ? 'gg' : ''}`}
                  style={selectedEntry.platform === 'all' ? { background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)', fontSize: 10 } : { fontSize: 10 }}
                >
                  {platformIcons[selectedEntry.platform] || selectedEntry.platform}
                </span>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{station === 'v' ? '国家' : '市场'}</div>
                <div style={{ fontSize: 12, color: 'var(--text1)', fontWeight: 600 }}>{getMarketLabel(selectedEntry.market)}</div>
              </div>
              {selectedEntry.object_level && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>适用层级</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedEntry.object_level.split(',').filter(Boolean).map(v => (
                      <span key={v} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>{v}</span>
                    ))}
                  </div>
                </div>
              )}
              {(getApplicableStatus(selectedEntry, 'campaign') || getApplicableStatus(selectedEntry, 'adset') || getApplicableStatus(selectedEntry, 'ad')) && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>适用状态</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {getApplicableStatus(selectedEntry, 'campaign') && (
                      <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>
                        系列状态：{getApplicableStatus(selectedEntry, 'campaign')}
                      </span>
                    )}
                    {getApplicableStatus(selectedEntry, 'adset') && (
                      <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>
                        广告组状态：{getApplicableStatus(selectedEntry, 'adset')}
                      </span>
                    )}
                    {getApplicableStatus(selectedEntry, 'ad') && (
                      <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>
                        广告状态：{getApplicableStatus(selectedEntry, 'ad')}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {selectedEntry.object_account_ids && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>适用广告账户</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {getAccountLabels(selectedEntry.platform || 'all', selectedEntry.object_account_ids).map(v => (
                      <span key={v} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>{v}</span>
                    ))}
                  </div>
                </div>
              )}
              {selectedEntry.objective_type && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>广告目标</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedEntry.objective_type.split(',').filter(Boolean).map(v => (
                      <span key={v} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>{v}</span>
                    ))}
                  </div>
                </div>
              )}
              {selectedEntry.object_tag && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>明细类型</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedEntry.object_tag.split(',').filter(Boolean).map(v => (
                      <span key={v} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>{v}</span>
                    ))}
                  </div>
                </div>
              )}
              {selectedEntry.scope_tag && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>广告产品</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedEntry.scope_tag.split(',').filter(Boolean).map(v => (
                      <span key={v} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(0,0,0,.04)', color: 'var(--text2)', border: '1px solid var(--border)' }}>{v}</span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>来源</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: selectedEntry.source === 'ai_learned' ? 'var(--primary-light)' : 'var(--text2)' }}>
                  {selectedEntry.source === 'ai_learned' ? '🤖 AI 学习' : '✍ 人工录入'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>录入日期</div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>{selectedEntry.created_at?.split('T')[0]}</div>
              </div>
            </div>
            {/* Description */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8 }}>描述</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>{displayText(selectedEntry.description)}</div>
            </div>
            {selectedEntry.diagnostic_tags && selectedEntry.diagnostic_tags.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--green)', marginBottom: 8 }}>诊断标签</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedEntry.diagnostic_tags.map(tag => (
                    <span key={tag} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 999, background: 'rgba(16,185,129,.08)', color: '#059669', border: '1px solid rgba(16,185,129,.18)' }}>{tag}</span>
                  ))}
                </div>
              </div>
            )}
            {/* Trigger conditions */}
            {canViewFullKnowledge && selectedEntry.trigger_condition && (() => {
              const rules = parseTriggerRules(selectedEntry.trigger_condition);
              return (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 8 }}>触发条件</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {rules.map((r, i) => (
                      <div key={i} style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, padding: '8px 12px', background: 'rgba(245,158,11,.06)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(245,158,11,.15)' }}>
                        {renderTriggerRule(r)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {!canViewFullKnowledge && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 8 }}>触发条件</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, padding: '8px 12px', background: 'rgba(245,158,11,.06)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(245,158,11,.15)' }}>
                  需 Leader 权限查看完整规则
                </div>
              </div>
            )}
            {selectedEntry.counter_conditions && selectedEntry.counter_conditions.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--danger, #ef4444)', marginBottom: 8 }}>反例条件</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedEntry.counter_conditions.map(tag => (
                    <span key={tag} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 999, background: 'rgba(239,68,68,.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,.18)' }}>{tag}</span>
                  ))}
                </div>
              </div>
            )}
            {/* Action/Effect */}
            {selectedEntry.action && (
              <div style={{ padding: 16, background: 'var(--primary-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(18,16,184,.1)', marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
                  <strong style={{ color: 'var(--primary-light)' }}>应用效果</strong>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>{displayText(selectedEntry.action)}</div>
              </div>
            )}
            {/* Priority + activation mode */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {selectedEntry.priority && (
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                  color: selectedEntry.priority === 'high' ? 'var(--danger, #ef4444)' : selectedEntry.priority === 'low' ? 'var(--text3)' : 'var(--amber)',
                  background: selectedEntry.priority === 'high' ? 'rgba(239,68,68,.08)' : selectedEntry.priority === 'low' ? 'rgba(0,0,0,.03)' : 'rgba(245,158,11,.08)',
                  border: '1px solid var(--border)',
                }}>
                  {selectedEntry.priority === 'high' ? '⬆ 高优先级' : selectedEntry.priority === 'low' ? '⬇ 低优先级' : '● 中优先级'}
                </span>
              )}
              {selectedEntry.activation_mode && (
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600, color: 'var(--cyan)', background: 'rgba(13,148,136,.08)', border: '1px solid var(--border)' }}>
                  {selectedEntry.activation_mode === 'immediate' ? '⚡ 立即生效' : selectedEntry.activation_mode === 'gradual' ? '🧪 灰度验证' : '📋 仅记录'}
                </span>
              )}
            </div>
            {/* Recent AI application logs */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 10 }}>近期AI应用记录</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,.03)' }}>
                    <th style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--text3)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>时间</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--text3)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>场景</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--text3)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>AI动作</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--text3)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>结果</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={4} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>暂无应用记录</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {/* Footer */}
          <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
            {canWriteKnowledge ? (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--danger, #ef4444)', fontSize: 12 }}
                onClick={() => handleArchive(selectedEntry.id)}
              >
                归档
              </button>
            ) : <div />}
            <div style={{ display: 'flex', gap: 8 }}>
              {canWriteKnowledge && selectedEntry.status === 'candidate' && (
                <button className="btn btn-primary btn-sm" onClick={() => handlePromote(selectedEntry.id)}>采纳</button>
              )}
              <button className="btn btn-outline btn-sm" onClick={() => setSelectedEntry(null)}>关闭</button>
              {canWriteKnowledge && (
                <button className="btn btn-primary btn-sm" onClick={e => { setSelectedEntry(null); openEdit(selectedEntry, e); }}>编辑</button>
              )}
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
